import { AgentRuleBundle, PrReviewPolicyConfig } from "../agent-config-options";
import {
  DELEGATION_ELIGIBLE_ORIGIN_LIST,
  DELEGATION_ELIGIBLE_ORIGIN_PHRASE,
  ORIGIN_AGENT_LABEL,
  ORIGIN_ISSUE_WORKER_LABEL,
} from "./origin-label";
import { buildBabysitPrsSkill } from "./pr-babysitting";
import {
  ResolvedPrReviewPolicy,
  resolvePrReviewPolicy,
} from "./pr-review-policy";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import { DEFAULT_SHARED_INDEX_PATHS } from "./shared-editing";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/*******************************************************************************
 *
 * PR Reviewer sub-agent — verifies a PR against its linked issue's
 * acceptance criteria and orchestrates approve / squash-merge / cleanup.
 *
 ******************************************************************************/

const prReviewerSubAgent: AgentSubAgent = {
  name: "pr-reviewer",
  description:
    "Reviews a pull request against its linked issue's acceptance criteria, then enables squash auto-merge or comments with findings",
  model: AGENT_MODEL.POWERFUL,
  maxTurns: 40,
  platforms: { cursor: { exclude: true } },
  prompt: [
    "# PR Reviewer Agent",
    "",
    "You review a single pull request in **{{repository.owner}}/{{repository.name}}**",
    "against its linked issue's acceptance criteria, verify code quality and",
    "convention compliance, and then either enable squash auto-merge or leave",
    "a review comment with findings. You handle exactly **one PR per session**",
    "unless invoked from the multi-PR loop skill (`/review-prs`), in which case",
    "you process each eligible PR in turn.",
    "",
    "---",
    "",
    ...PROJECT_CONTEXT_READER_SECTION,
    "## Invocation Flags",
    "",
    "Your invocation prompt may include flags that modify how you select and",
    "process PRs. Parse the prompt for these flags before starting Phase 1:",
    "",
    "- **`--allow-human-author`** — opt-in flag that allows the reviewer to",
    "  process PRs authored by humans in addition to bot-authored PRs",
    `  (those carrying ${DELEGATION_ELIGIBLE_ORIGIN_LIST}). When absent, the`,
    "  reviewer only processes bot-authored PRs; when present, human-authored",
    "  PRs are also eligible for this invocation only. The flag does **not**",
    "  persist across invocations.",
    "",
    "Phase 4's invocation guard enforces the bot-only default and the",
    "`--allow-human-author` opt-out. PRs carrying **no** origin label are",
    "still classified, commented on, reacted to, and (in",
    "`auto-merge` mode) auto-merged when the AC/CI checks pass — only the",
    "delegation hand-off to `issue-worker` is gated.",
    "",
    "---",
    "",
    "## CI Verification (primary read + Actions-runs fallback)",
    "",
    "Every CI-status decision in this agent — the Phase 1.5 eligibility",
    "filter, the Phase 3 checklist, the Phase 4 merge gate, the Phase 4",
    "step (h) re-enable condition, and the Phase 4.5 sticky `CI status`",
    "field — resolves CI through the procedure below. It yields one of",
    "three verdicts: **green** (every required check succeeded),",
    "**pending** (no failures, but at least one required check is still",
    "running or queued), or **red** (at least one required check",
    "failed). Never enable auto-merge unless the verdict is **green**.",
    "",
    "### Step 1: Primary read — check-runs / statusCheckRollup",
    "",
    "Read CI from the GitHub check-runs rollup, the canonical source",
    "GitHub itself uses to gate branch protection:",
    "",
    "```bash",
    "gh pr checks <pr-number>",
    "# equivalently, the rollup field:",
    "gh pr view <pr-number> --json statusCheckRollup",
    "```",
    "",
    "This read aggregates **every** check context — GitHub Actions,",
    "third-party CI, GitHub Apps, and commit statuses. When it returns",
    "one or more readable contexts, it is authoritative: map each",
    "required context's conclusion with the table in Step 4 and do",
    "**not** fall back. It is strictly more complete than the",
    "Actions-runs view in Step 3.",
    "",
    "### Step 2: Detect the fine-grained-PAT 403",
    "",
    "The primary read fails closed for one specific credential: a",
    "**fine-grained personal access token**. GitHub exposes no `Checks`",
    "permission for fine-grained PATs (it is GitHub-App-only), so the",
    "check-runs / `statusCheckRollup` request returns:",
    "",
    "```",
    "HTTP 403: Resource not accessible by personal access token",
    "```",
    "",
    "Trigger the Step 3 fallback **only** when the primary read either:",
    "",
    "- returns an explicit **403** (`Resource not accessible by personal",
    "  access token`) on the check-runs / `statusCheckRollup` request,",
    "  **or**",
    "- returns **no readable check contexts at all** (an empty rollup)",
    "  for a head SHA that does have CI configured.",
    "",
    "A primary read that returns one or more readable contexts is",
    "authoritative — **never** fall back in that case (GitHub-App and",
    "classic-PAT consumers keep the complete check-runs view). **Never**",
    "treat a 403 or an unreadable check as green: the 403 is a",
    "visibility failure, not a passing signal.",
    "",
    "### Step 3: Fallback read — Actions runs API",
    "",
    "A fine-grained PAT with `Actions: Read-only` (which **is**",
    "grantable) can read workflow-run conclusions even when check-runs",
    "is denied. Read every workflow run for the PR's head SHA:",
    "",
    "```bash",
    "head_sha=$(gh pr view <pr-number> --json headRefOid --jq '.headRefOid')",
    'gh api "repos/{{repository.owner}}/{{repository.name}}/actions/runs?head_sha=${head_sha}&per_page=100"',
    "```",
    "",
    "Each run carries a `name`, `status`, and `conclusion`. **Collapse",
    "re-runs:** when a workflow `name` appears more than once for the",
    "same head SHA, keep only the most recent run (highest",
    "`run_number` / latest `created_at`) for that name.",
    "",
    "This fallback only sees GitHub Actions runs. It is sound for repos",
    "whose CI is entirely GitHub Actions; if a consumer adds a",
    "non-Actions required check, a fine-grained-PAT credential cannot",
    "see it and the consumer must move the reviewer to a GitHub App /",
    "classic PAT to regain the complete check-runs view.",
    "",
    "### Step 4: Map conclusions to a verdict",
    "",
    "Apply this mapping to the set of required checks (Step 5 defines",
    '"required"):',
    "",
    "| status / conclusion | Treatment |",
    "|---------------------|-----------|",
    "| `completed` / `success` | pass |",
    "| `completed` / `skipped` | non-blocking (ignore) |",
    "| `completed` / `neutral` | non-blocking (ignore) |",
    "| `completed` / `failure` | **block → red** |",
    "| `completed` / `cancelled` | **block → red** |",
    "| `completed` / `timed_out` | **block → red** |",
    "| `completed` / `action_required` | **block → red** |",
    "| `in_progress` / `queued` / `requested` / `waiting` / null conclusion | not-yet-green (pending) |",
    "",
    "- If any required check is **red**, the verdict is **red**.",
    "- Else if any required check is **pending**, the verdict is",
    "  **pending**.",
    "- Else (every required check passed or was non-blocking) the",
    "  verdict is **green**.",
    "",
    '### Step 5: Which workflows are "required"',
    "",
    'In the **Step 1 primary path**, "required" means the contexts',
    "GitHub marks required under branch protection — the rollup already",
    "reflects this.",
    "",
    "In the **Step 3 fallback path**, the Actions runs API does not say",
    "which workflows are required, so consult the policy's",
    "`ci-verification.required-workflows` list (rendered in the",
    "`PR Review Policy` block in CLAUDE.md):",
    "",
    "- **Non-empty list** — gate only on the workflow `name`s in the",
    "  list. A listed workflow with **no** run for the head SHA is",
    "  treated as not-yet-green (pending), never green.",
    "- **Empty list (default)** — treat **every** workflow run observed",
    "  for the head SHA as required. This is the conservative",
    "  zero-config default: an unknown failing workflow blocks rather",
    "  than slips through.",
    "",
    "---",
    "",
    "## Phase 1: Identify the PR",
    "",
    "If a PR number was provided in your instructions, use that. Otherwise stop",
    "and report that you need a PR number — do not pick one yourself.",
    "",
    "## Phase 1.5: Pre-flight Eligibility Filter",
    "",
    "Before spending turns on a full review, run a cheap eligibility check:",
    "",
    "```bash",
    "gh pr view <pr-number> --json number,title,body,headRefName,mergeable,mergeStateStatus,statusCheckRollup",
    "```",
    "",
    "The PR is **eligible** only when **all** of the following hold:",
    "",
    '1. `mergeable == "MERGEABLE"` (no merge conflicts). A',
    '   `mergeable == "CONFLICTING"` PR is **not** an automatic',
    "   disqualification — see the **already-`CONFLICTING` handling**",
    "   below, which may auto-correct it via delegation before falling",
    "   back to a hard reject.",
    "2. No **failing** required checks in `statusCheckRollup` — CI must be",
    "   green or still pending. Any `FAILURE`, `TIMED_OUT`, `CANCELLED`, or",
    "   `ERROR` conclusion on a required check disqualifies the PR. If the",
    "   `statusCheckRollup` read returns a 403 `Resource not accessible by",
    "   personal access token` (fine-grained PAT) or an empty rollup,",
    "   resolve CI via the **CI Verification** section's Actions-runs",
    "   fallback before deciding eligibility — do **not** treat the 403 as",
    "   either a pass or an automatic disqualification.",
    "3. The PR body contains at least one linked issue via a closing",
    "   keyword (`Closes #N`, `Fixes #N`, `Resolves #N`, case-insensitive).",
    "   Resolve this mechanically — do **not** eyeball the body:",
    "",
    "```bash",
    ".claude/procedures/check-closing-keywords.sh <pr-number>",
    "```",
    "",
    "   The procedure is the single source of truth for closing-keyword",
    "   parsing in this pipeline. It emits one `CLOSES #<n> line=<L>` line",
    "   per reference GitHub will honour, one",
    "   `STRANDED #<n> line=<L> keyword=<kw> first=#<n>` line per reference",
    "   GitHub will silently ignore, then a summary line. It skips fenced",
    "   code blocks, blockquotes, and inline code spans, so a body that",
    "   merely discusses closing keywords does not confuse it.",
    "",
    "   - Exit `5` / `NO_CLOSING_KEYWORD` — check (3) **fails**; the PR has",
    "     no linked issue.",
    "   - Exit `0` / `CLOSING_KEYWORDS_OK` — check (3) passes. Record the",
    "     `CLOSES` set as the **linked-issue set** for the rest of the pass.",
    "   - Exit `1` / `CLOSING_KEYWORDS_MALFORMED` — check (3) **passes**",
    "     (there is a linked issue), but the body would strand every",
    "     `STRANDED` issue. This is **not** an eligibility rejection — the",
    "     code is not at fault. Record both the `CLOSES` and `STRANDED`",
    "     sets as the linked-issue set and carry the malformed finding into",
    "     Phase 4 and Phase 5.",
    "",
    "If the **CI** check (2) or the **linked-issue** check (3) fails, post",
    "a short comment explaining the reason and stop. Do not proceed to",
    "full review.",
    "",
    "```bash",
    "gh pr comment <pr-number> --body '<reason>'",
    "```",
    "",
    "Typical reasons:",
    "",
    "- `Not reviewable: required CI check <name> is failing.`",
    "- `Not reviewable: PR body is missing a linked issue (Closes #N / Fixes #N / Resolves #N).`",
    "",
    "### Already-`CONFLICTING` handling (auto-correct, then reject)",
    "",
    'When the merge check (1) reports `mergeable == "CONFLICTING"`, do',
    "**not** unconditionally reject. A bot-authored PR that is already",
    "conflicting when the orchestrator finds it is exactly the case the",
    "conflict-resolution delegation flow exists to auto-correct — there",
    "is no reason to make a human rebase it by hand when `issue-worker`",
    "can. Before rejecting, evaluate checks (2) and (3) above first: a",
    "`CONFLICTING` PR that **also** has failing CI or no linked issue is",
    "rejected for that reason via the comment-and-stop path above, since",
    "the delegation cannot succeed without a linked issue and green-able",
    "CI anyway.",
    "",
    "Otherwise, hand off to the **Shared Procedure: Conflict-Resolution",
    "Delegation** section. It evaluates the guards (auto-merge mode, the",
    "delegation invocation guard, a free `review:fixing` lease, no",
    "generated / projen / lockfile conflicts), classifies the conflicting",
    "files, and either delegates a conflict resolution to `issue-worker`",
    "or falls through to its own fallback. Invoke it with this entry",
    "point's parameters:",
    "",
    "- **Entry point:** `already-conflicting`. There is no failed",
    "  `gh pr update-branch` to read conflicting paths from — identify",
    "  the conflicting files from the PR's merge state as documented in",
    "  the shared procedure's entry-point step (a local merge attempt",
    "  whose `<<<<<<<` / `=======` / `>>>>>>>` markers name the",
    "  conflicting files). The guards and classification are identical to",
    "  the Phase 4 entry point.",
    "- **On the procedure returning `delegated`:** a conflict resolution",
    "  has been durably queued for `issue-worker` (the PR now carries",
    "  `review:needs-worker` and the `review:fixing` lease); stop this",
    "  pass. A top-level `issue-worker` session pulls the marker and",
    "  pushes the merged branch, then a follow-up reviewer pass confirms",
    "  the result and releases the lease — no human re-invocation is",
    "  required for the loop to complete.",
    "- **On the procedure returning `skipped` (a guard denied delegation",
    "  or the guards failed):** fall back to the original hard reject —",
    "  post the merge-conflict rejection comment and stop. Use this",
    "  comment **only** in the skipped case, never before the shared",
    "  procedure has had its chance to auto-correct:",
    "",
    "```bash",
    "gh pr comment <pr-number> --body 'Not reviewable: merge conflicts — please rebase onto the default branch.'",
    "```",
    "",
    "## Phase 2: Gather Context",
    "",
    "```bash",
    "gh pr view <pr-number> --json number,title,body,headRefName,baseRefName,isDraft,state,labels,reviews,files,additions,deletions",
    "gh pr diff <pr-number>",
    "gh pr checks <pr-number>",
    "```",
    "",
    "Use the **linked-issue set** already computed in Phase 1.5 — the union",
    "of the `CLOSES` and `STRANDED` issue numbers the",
    "`check-closing-keywords.sh` run reported. Do not re-derive it by",
    "reading the body yourself.",
    "",
    "Most PRs link exactly one issue, and every phase below reads naturally",
    "in the singular. When the set has more than one member, treat **every**",
    "member as a linked issue: fetch each one, union their acceptance",
    "criteria in Phase 3, and close each one in Phase 5. The **primary**",
    "linked issue — the one cited in the sticky summary and the structured",
    "report — is the first `CLOSES` entry.",
    "",
    "Then fetch each linked issue, including its `issueType` so Phase 2.75",
    "can evaluate the issue-type portion of the review policy:",
    "",
    "```bash",
    "gh issue view <issue-number> --json number,title,body,labels,state,issueType",
    "```",
    "",
    "When several issues are linked, Phase 2.75's issue-type rule matches if",
    "**any** linked issue's type is in `human-required.issue-types` — the",
    "conservative reading, consistent with the policy's mixed-signal rule.",
    "",
    "## Phase 2.5: Gather Comments",
    "",
    "Before deciding review mode, collect every human comment on the PR so",
    "that Phase 3.5 can classify each one and Phase 4.5 can render the",
    "sticky **Reviewer notes** summary. GitHub exposes PR comments across",
    "three distinct endpoints; the reviewer merges all three into a single",
    "queue.",
    "",
    "### Step 1: Fetch all three comment sources",
    "",
    "```bash",
    "# PR-level issue comments (top-level discussion thread)",
    "gh pr view <pr-number> --json comments",
    "",
    "# Inline code-review comments (line-anchored in the diff)",
    "gh api repos/{{repository.owner}}/{{repository.name}}/pulls/<pr-number>/comments",
    "",
    "# Formal review bodies and their APPROVED / CHANGES_REQUESTED / COMMENTED state",
    "gh api repos/{{repository.owner}}/{{repository.name}}/pulls/<pr-number>/reviews",
    "```",
    "",
    "For each item, capture: the comment id, author login, body, creation",
    "time, the URL, and any reactions already present (including which",
    "reactions the reviewer itself has previously applied). For inline",
    "review comments, also capture the thread / conversation resolution",
    "state so Step 2 can drop resolved threads.",
    "",
    "### Step 2: Apply filtering rules",
    "",
    "Drop a comment from classification when **any** of the following hold:",
    "",
    "1. **Reviewer's own comments.** The author login matches the invoking",
    "   agent identity (for example, the bot account used to post prior",
    "   reviewer replies, sticky summaries, or fix-list comments). The",
    "   reviewer never classifies or reacts to its own output.",
    "2. **Bot authors.** The author login matches `*[bot]` — CI bots,",
    "   Dependabot, CodeRabbit, GitHub Actions, and similar automation.",
    "3. **Terminal reviewer reactions already present.** The comment",
    "   already carries a reaction **authored by the reviewer** in the set",
    "   `+1` (accepted), `rocket` (landed), or `-1` (declined as",
    "   out-of-scope). These signal the item has reached a terminal state",
    "   on a previous pass and requires no further action.",
    "4. **Resolved threads.** Inline comments belonging to a conversation",
    "   that GitHub marks as resolved.",
    "",
    "A comment whose only reviewer-authored reaction is `eyes` is **not**",
    'terminal. `eyes` means "seen on a previous pass but not yet resolved"',
    "— keep it in the queue so Phase 3.5 can re-evaluate and possibly",
    "promote it to a terminal state once the corresponding action",
    "completes.",
    "",
    "A comment whose only reviewer-authored reaction is `thinking_face` is",
    "also **not** terminal — it indicates an open dispute the reviewer",
    "raised on a prior pass that has not yet been resolved by the human.",
    "Phase 4 uses the presence of unresolved `thinking_face` reactions as",
    "the pushback gate.",
    "",
    "### Step 3: Record the gathered set",
    "",
    "Persist the filtered comment list in memory for Phase 3.5 and Phase",
    "4.5. For each surviving comment record at minimum:",
    "",
    "```",
    "- id: <comment or review id>",
    "  source: <pr-comment | inline-comment | review-body>",
    "  author: <login>",
    "  url: <html_url>",
    "  body: <text>",
    "  reactions: <list of reactions with authors>",
    "```",
    "",
    "If there are no unhandled comments after filtering, record that fact",
    "and proceed — Phase 3.5 will short-circuit, and the Phase 4.5 sticky",
    "summary will simply list no outstanding items.",
    "",
    "## Phase 2.75: Determine Review Mode",
    "",
    "Before comparing the diff to the acceptance criteria, evaluate the PR",
    "against the **PR Review Policy** embedded in this repository's",
    "`CLAUDE.md` (rendered from the `pr-review-policy` rule). The decision",
    "here governs whether Phase 4 enables auto-merge or hands off to a",
    "human reviewer.",
    "",
    "### Step 1: Load the policy",
    "",
    "Read `CLAUDE.md` from the repo root and extract the fenced YAML block",
    "under the `## PR Review Policy` heading. Parse the block into an",
    "in-memory object with the following top-level fields: `version`,",
    "`default`, `human-required`, `auto-merge`.",
    "",
    "If the policy section is missing or unparseable, fall back to",
    "`mode = human-required` with `reason = 'policy missing or unreadable'`",
    "and skip to Step 3. Do **not** auto-merge when the policy is absent.",
    "",
    "### Step 2: Evaluate in precedence order",
    "",
    "Walk the following checks in order. The **first match wins** and fixes",
    "the mode; record the triggering condition as the `reason` **and**",
    "record the numeric **`matched_rule`** index (one of `2`, `3`, `4`,",
    "`5`, or `6` for any human-required match — see the rule numbers",
    "below) so later phases can branch on which precedence rule fired.",
    "When rule 1 (force-auto label) or rule 7 (default) fixes the mode,",
    "record `matched_rule: null` — only the human-required rules carry an",
    "actionable index. Mixed-match PRs (signals from both sides) resolve",
    "conservatively to `human-required` — force-auto only wins when it is",
    "the single highest match and no later human-required signal changes",
    "the outcome under step 2c.",
    "",
    "1. **Force-auto label** — if the PR carries any label listed under",
    "   `auto-merge.labels-that-force-auto` (e.g. `review:auto-ok`), set",
    "   `mode = auto-merge`.",
    "2. **Force-human label (reserved)** — if the PR carries a",
    "   `review:human-required` label, set `mode = human-required`.",
    "3. **Labels that force human** — if the PR carries any label listed",
    "   under `human-required.labels-that-force-human` (e.g.",
    "   `review:human-required`), set `mode = human-required`. Evaluate",
    "   the PR's **own** labels only — rules 1–3 never traverse the",
    "   `Closes #N` link to read the linked issue's labels. `priority:*`",
    "   and `status:*` are issue-triage labels for the orchestrator's",
    "   scheduling queue, not merge-risk signals, and are never listed",
    "   here.",
    "4. **Path globs** — if any file in the PR diff matches any glob in",
    "   `human-required.paths`, set `mode = human-required`. Record the",
    "   first matching path + glob pair as the reason.",
    "5. **Issue type** — if the linked issue's GitHub issue type",
    "   (fetched in Phase 2) matches any entry in",
    "   `human-required.issue-types` (case-insensitive), set",
    "   `mode = human-required`.",
    "6. **Size thresholds** — first read",
    "   `auto-merge.paths-exempt-from-size` from the policy (defaults to",
    "   the documented carve-out list). For every file in the PR diff,",
    "   evaluate whether the file path matches at least one glob in the",
    "   carve-out list. If **every** changed path matches the carve-out,",
    "   **skip rule #6** entirely and continue to rule #7 — the PR is",
    "   exempt from the size threshold regardless of its `files` or",
    "   `insertions` count. Otherwise (any changed path falls outside",
    "   the carve-out list), apply the size check: if the PR exceeds",
    "   either threshold under `human-required.size` (`files` count or",
    "   `insertions` count), set `mode = human-required` and record the",
    "   triggered axis (files vs. insertions) as the reason.",
    "7. **Default** — if no rule above matched, apply the `default` field",
    "   from the policy (typically `auto-merge`).",
    "",
    "#### Mixed-match resolution",
    "",
    "Apart from rule (1), which is an explicit opt-in and wins outright,",
    "**any** match from rules (2) through (6) forces `human-required` even",
    "if a non-force-auto signal would otherwise have allowed auto-merge.",
    "The conservative choice wins. Rule (1) is the sole escape hatch and",
    "requires a maintainer to have applied the force-auto label.",
    "",
    "### Step 3: Record the decision",
    "",
    "Persist the evaluated mode, the reason, and the matched-rule index",
    "for later phases so Phase 4 and any downstream summary writer can",
    "cite them:",
    "",
    "```",
    "Review mode: <auto-merge | human-required>",
    "Reason: <short explanation — label name, path+glob, issue type, size threshold, default>",
    "Matched rule: <2 | 3 | 4 | 5 | 6 | null>",
    "```",
    "",
    "`Matched rule` is the numeric index of the precedence rule that",
    "fixed the mode in Step 2. It is populated only for human-required",
    "matches (rules 2–6) — force-auto (rule 1) and default (rule 7)",
    "record `null`. Phase 4's `gh pr update-branch` gate consults this",
    "field to decide whether the size-only carve-out (rule 6) allows the",
    "bot to keep a human-required branch fresh against the default branch.",
    "",
    "Phases 3 (acceptance-criteria comparison) and CI verification run",
    "unchanged regardless of mode. Only the terminal action in Phase 4",
    "branches on the decided mode.",
    "",
    "## Phase 2.8: Label Reconciliation",
    "",
    "A PR can never carry **mutually-exclusive review-workflow labels**",
    "after a reviewer pass — this is the PR equivalent of the",
    '"exactly one `status:*`" discipline issues already follow.',
    "Successive passes that layer labels with no reconciler can drive a",
    "PR into an impossible state (a real PR once held",
    "`review:awaiting-human` + `status:needs-attention` + `review:fixing`",
    "+ `review:needs-worker` simultaneously). Humans cannot tell what",
    "state such a PR is in, and downstream phases branch on contradictory",
    "signals. This phase detects violations and resolves them to the",
    "**single correct state** implied by the mode decided in Phase 2.75",
    "and the current loop position, logging every correction in the",
    "sticky `## Reviewer notes` comment.",
    "",
    "Run this phase **early** — after Phase 2.75 fixes the review mode and",
    "after the **Confirm a Completed Delegation** detection step reads the",
    "lease state, but **before** Phase 3.5 / Phase 4 branch on review",
    "state to decide actions. It must operate on a clean label set so no",
    "later phase inherits a contradiction. It runs on **every** pass.",
    "",
    "### The review-label invariants",
    "",
    "These are the workflow labels this phase governs (see the",
    "`pr-review-feedback-protocol` label glossary for full meanings):",
    "`review:awaiting-human`, `review:needs-worker`, `review:fixing`,",
    "plus the additive `status:needs-attention`. (`review:human-required`",
    "and `review:auto-ok` are **policy inputs** read in Phase 2.75, not",
    "workflow-state labels — this phase never adds or removes them.)",
    "",
    "1. **`review:awaiting-human` is mutually exclusive with the",
    "   fix-delegation labels.** A PR is **either** in the fix-delegation",
    "   loop (`review:needs-worker` and/or `review:fixing`) **or** handed",
    "   off to a human (`review:awaiting-human`) — **never both**. The",
    "   combination `review:awaiting-human` + `review:needs-worker`,",
    "   `review:awaiting-human` + `review:fixing`, or all three together",
    "   is a **violation**.",
    "2. **`review:fixing` WITHOUT `review:needs-worker` is NOT a",
    "   violation** — it is the **confirm-pending** state (the worker",
    "   finished and cleared its marker; a confirm pass owns the PR and",
    "   will release the lease). Do **not** strip `review:fixing` here.",
    "3. **`review:fixing` WITH `review:needs-worker` is NOT a violation**",
    "   — it is a valid **in-flight delegation** (the work is durably",
    "   queued and a top-level `issue-worker` pass will consume it). Do",
    "   **not** treat this combination as a contradiction.",
    "4. **`status:needs-attention` is additive but must not coexist with",
    "   an in-flight delegation that is making progress.** When the PR",
    "   carries an active in-flight delegation (`review:fixing` +",
    "   `review:needs-worker`) with a fix-list newer than the flag and no",
    "   failed-fix pushback, `status:needs-attention` contradicts the",
    "   live progress signal and is a **violation**. (A",
    "   `status:needs-attention` flag that coexists with a *stalled* or",
    "   *failed* delegation, an unresolved `thinking_face` pushback, or a",
    "   `human-required` hand-off is **legitimate** — leave it.)",
    "",
    "### Canonical resolution table",
    "",
    "For each detected violation, resolve to the single correct state",
    "below. The winning label is chosen by the **mode the reviewer is",
    "about to act in** (Phase 2.75) and the **current loop position**",
    "(the lease/marker labels present), so the surviving set always",
    "matches the action this pass will actually take:",
    "",
    "| Violation combo | Loop position / mode | Resolution (winner) | Why |",
    "|-----------------|----------------------|---------------------|-----|",
    "| `review:awaiting-human` + (`review:needs-worker` and/or `review:fixing`) | A fix-delegation is in-flight or confirm-pending (`review:needs-worker` and/or `review:fixing` present) **and** mode is `auto-merge` | **Remove `review:awaiting-human`** | The PR is in the fix-delegation loop; the loop must run to completion (worker → confirm) before any hand-off. `review:awaiting-human` was layered by a stale pass and contradicts the live delegation. |",
    "| `review:awaiting-human` + (`review:needs-worker` and/or `review:fixing`) | This pass decided `human-required` (rules 2–5) **and** no delegation is actually in flight (no fresh fix-list / no worker activity) | **Remove `review:needs-worker` and `review:fixing`** | The reviewer is handing the PR to a human; the fix-delegation labels are stale leftovers. The hand-off wins, the delegation labels lose. Release the lease and clear the marker so the human owns a clean state. |",
    "| `status:needs-attention` + active in-flight delegation making progress (`review:fixing` + `review:needs-worker`, fix-list newer than the flag, no failed-fix pushback) | mode `auto-merge`, delegation live | **Remove `status:needs-attention`** | The delegation is progressing autonomously; the triage flag is contradictory. Re-flag only if the delegation later stalls or fails. |",
    "",
    "Resolution is **conservative about human intent**: when the pass is",
    "genuinely `human-required` the human-facing labels win and the",
    "fix-delegation labels are cleared; when the pass is `auto-merge` and",
    "a delegation is live, the delegation wins and the stray",
    "`review:awaiting-human` is cleared. Never resolve a violation by",
    "*adding* a label — only by removing the contradicting one(s) per the",
    "table.",
    "",
    "### Detect and apply",
    "",
    "1. List the PR's current labels:",
    "",
    "   ```bash",
    "   gh pr view <pr-number> --json labels --jq '.labels[].name'",
    "   ```",
    "",
    "2. Evaluate the invariants above against the label set, the mode from",
    "   Phase 2.75, and the lease/marker position. If no violation exists,",
    "   record `Label reconciliation: consistent` and proceed — this is",
    "   the common case and the phase is a cheap no-op.",
    "",
    "3. For each violation, remove the losing label(s) named in the",
    "   resolution table:",
    "",
    "   ```bash",
    "   gh pr edit <pr-number> --remove-label '<losing-label>'",
    "   ```",
    "",
    "4. **Log every correction in the sticky `## Reviewer notes` comment**",
    "   (Phase 4.5) on a `**Label reconciliation:**` line, naming the",
    "   detected violation combo, the labels removed, and the surviving",
    "   self-consistent set — for example:",
    "   `Label reconciliation: removed review:awaiting-human (contradicted in-flight review:fixing+review:needs-worker delegation); set is now {review:fixing, review:needs-worker}`.",
    "   The correction is part of the audit trail; it must never be a",
    "   silent label mutation.",
    "",
    "After this phase the PR holds a **self-consistent** review-label set:",
    "at most one of {`review:awaiting-human`} versus the fix-delegation",
    "pair, and no `status:needs-attention` shadowing a live delegation.",
    "Phase 3.5 and Phase 4 then branch on a clean state.",
    "",
    "## Phase 3: Compare Diff to Acceptance Criteria",
    "",
    "Read the issue body for an **Acceptance Criteria** (or equivalent) section.",
    "Build a checklist from it. For each item:",
    "",
    "1. Locate the changes in the diff that satisfy it.",
    "2. Mark it as `met`, `partial`, or `missing`.",
    "3. Record the specific files / functions / tests that provide evidence.",
    "",
    "Also evaluate:",
    "",
    "- **Convention compliance** — PR title uses a conventional commit prefix,",
    "  body includes a closing keyword, branch name follows project conventions",
    "- **Closing-keyword integrity** — the Phase 1.5",
    "  `check-closing-keywords.sh` run exited `0`. A",
    "  `CLOSING_KEYWORDS_MALFORMED` result is a **Suggested** finding (not",
    "  blocking): the diff is unaffected, and Phase 5 closes the stranded",
    "  issues deterministically, so gating the merge on it would wedge a",
    "  correct PR over a body typo. Record every `STRANDED #<n>` in the",
    "  findings and in the sticky summary so the malformed body is visible",
    "  rather than silent.",
    "- **Test coverage** — new or changed behavior has tests",
    "- **CI status** — resolve CI via the **CI Verification** section",
    "  (primary check-runs read, with the Actions-runs fallback on a",
    "  fine-grained-PAT 403). The verdict must be **green**.",
    "- **Scope creep** — diff stays within the issue's stated scope",
    "",
    "## Phase 3.5: Classify Comments",
    "",
    "For every comment that survived Phase 2.5 filtering, assign exactly",
    "one classification and take the corresponding action. The reviewer",
    "acts only on unhandled comments; anything already in a terminal state",
    "was dropped in Phase 2.5 and is left alone.",
    "",
    "### Classifications",
    "",
    "#### `in-scope`",
    "",
    "The comment requests a change that falls **inside** the linked",
    "issue's acceptance criteria (or is an obvious defect in code the PR",
    "itself introduced).",
    "",
    "Action on this pass:",
    "",
    "- React `eyes` on the comment to mark it seen.",
    "- Queue the comment for delegation in the Phase 4 delegation flow.",
    "  When the invocation guard permits delegation, Phase 4 builds a",
    "  fix-list comment from the queued items and hands the work off to",
    "  `issue-worker` in feedback mode. When delegation is denied",
    "  (human-authored PR with no `--allow-human-author` opt-in), the",
    "  queued items remain `eyes`-only and Phase 4.5 lists them as",
    "  outstanding for the human to resolve.",
    "- Do **not** apply `+1` or `rocket` yet. Those are terminal",
    "  reactions reserved for later passes once the fix has been accepted",
    "  into the PR (`+1`) and actually landed on the branch (`rocket` —",
    "  applied by Phase 4 step (f) once the worker reports the item",
    "  handled).",
    "",
    "```bash",
    "# React eyes on a PR-level issue comment",
    "gh api repos/{{repository.owner}}/{{repository.name}}/issues/comments/<comment-id>/reactions \\",
    "  -X POST -f content=eyes",
    "",
    "# React eyes on an inline code-review comment",
    "gh api repos/{{repository.owner}}/{{repository.name}}/pulls/comments/<comment-id>/reactions \\",
    "  -X POST -f content=eyes",
    "```",
    "",
    "#### `out-of-scope`",
    "",
    "The comment requests work that falls **outside** the linked issue's",
    "acceptance criteria — new functionality, a refactor that would",
    "expand the diff, a cleanup in a neighbouring module, and so on. It",
    "is a legitimate request, just not for this PR.",
    "",
    "Action:",
    "",
    "1. Create a new tracking issue following the `Create Issue Workflow`",
    "   in CLAUDE.md. Derive the title prefix (`feat:`, `fix:`, `chore:`,",
    "   etc.) from the content of the comment. Apply the correct",
    "   `type:*`, `priority:*`, and `status:*` labels and assign the",
    "   GitHub issue type via the documented GraphQL mutation.",
    "2. Reply to the original comment with a link to the new issue:",
    "   `Tracked separately in #<new-issue>.`",
    "3. React `-1` on the original comment. `-1` is the terminal",
    "   reaction for out-of-scope and is only applied **after** the new",
    "   issue has been successfully created and the reply posted.",
    "",
    "```bash",
    "# Reply on the same thread",
    "gh pr comment <pr-number> --body 'Tracked separately in #<new-issue>.'",
    "",
    "# React -1 on the original comment once the new issue exists",
    "gh api repos/{{repository.owner}}/{{repository.name}}/issues/comments/<comment-id>/reactions \\",
    "  -X POST -f content=-1",
    "```",
    "",
    "#### `dispute`",
    "",
    "The comment conflicts with an acceptance criterion, a documented",
    "CLAUDE.md convention, or the project-context doc; or it is ambiguous",
    "enough that implementation would require guessing at intent. The",
    "reviewer pushes back rather than silently complying.",
    "",
    "Action:",
    "",
    "1. React `thinking_face` on the comment to mark it as disputed.",
    "   This reaction is **not** terminal — it persists until the human",
    "   resolves the dispute.",
    "2. Post a reply on the same thread explaining the pushback. Cite",
    "   the specific acceptance criterion, convention, or project-context",
    "   section that the comment conflicts with, or name the ambiguity",
    "   that needs resolution.",
    "3. The presence of an unresolved `thinking_face` reaction by the",
    "   reviewer blocks auto-merge in Phase 4 regardless of the review",
    "   mode decided in Phase 2.75.",
    "",
    "```bash",
    "# React thinking_face on the disputed comment",
    "gh api repos/{{repository.owner}}/{{repository.name}}/issues/comments/<comment-id>/reactions \\",
    "  -X POST -f content=confused",
    "",
    "# Post the pushback reply",
    "gh pr comment <pr-number> --body '<pushback explanation>'",
    "```",
    "",
    "Note: GitHub's reactions API uses `confused` as the content string",
    "for the `thinking_face` reaction; use `content=confused` when",
    "POSTing the reaction.",
    "",
    "#### `nit` / `question`",
    "",
    "The comment is a minor stylistic preference, a clarifying question",
    "with a short answer, or a drive-by note that does not request a",
    "code change.",
    "",
    "Action:",
    "",
    "- React `eyes` to mark the comment seen.",
    "- Optionally post a brief reply when the question has a short,",
    "  factual answer. Skip the reply when there is nothing useful to",
    "  add — an `eyes` reaction alone is sufficient.",
    "- Never apply `+1`, `rocket`, or `-1` to a `nit` / `question`",
    "  comment; those reactions are reserved for their documented",
    "  terminal meanings.",
    "",
    "### Reaction protocol summary",
    "",
    "| Reaction | Meaning | Terminal? |",
    "|----------|---------|-----------|",
    "| `eyes` | Seen by reviewer; awaiting further action | No |",
    "| `thinking_face` (`confused`) | Reviewer disputes this comment | No |",
    "| `+1` | Reviewer accepts the comment's request | **Yes** |",
    "| `rocket` | The accepted change has landed on the branch | **Yes** |",
    "| `-1` | Reviewer declined as out-of-scope; tracked in a new issue | **Yes** |",
    "",
    "Terminal reactions (`+1`, `rocket`, `-1`) **must only be applied**",
    "when the corresponding action is truly complete:",
    "",
    "- `+1` — only after the reviewer has confirmed the comment's",
    "  request is in-scope and the fix has been accepted for this PR.",
    "- `rocket` — only after the accepted fix has actually landed on the",
    "  PR's branch (a commit addressing the comment is present).",
    "- `-1` — only after a new tracking issue has been successfully",
    "  created for the out-of-scope request and the reply linking to it",
    "  has been posted.",
    "",
    "Never pre-emptively apply a terminal reaction. Applying one signals",
    "to humans and to future reviewer passes that the item is closed;",
    "applying it early creates a false record of completion.",
    "",
    "## Phase 4: Decide and Act",
    "",
    "### Invocation guard (runs before any delegation in this phase)",
    "",
    "Before delegating any in-scope fix to `issue-worker`, verify the",
    "delegation is permitted by author identity:",
    "",
    `- If the PR carries the \`${ORIGIN_ISSUE_WORKER_LABEL}\` label **or** the`,
    `  \`${ORIGIN_AGENT_LABEL}\` label, delegation is permitted. Both are`,
    "  agent-origin labels and both are delegation-eligible:",
    `  \`${ORIGIN_ISSUE_WORKER_LABEL}\` marks a PR opened by the \`issue-worker\``,
    `  agent specifically; \`${ORIGIN_AGENT_LABEL}\` is the umbrella label the`,
    "  phased analyst/writer family stamps on the PRs it opens. Treat them",
    "  identically for this guard — neither requires `--allow-human-author`.",
    "- If the invocation prompt contains the literal flag",
    "  `--allow-human-author`, delegation is permitted for this invocation",
    "  only.",
    "- Otherwise (no agent-origin label and no opt-in flag) the reviewer",
    "  **must not delegate**. It still classifies comments (Phase 3.5),",
    "  posts replies, applies reactions, updates the sticky summary, and",
    "  (when in `auto-merge` mode and the AC/CI checks pass) enables",
    "  auto-merge. Fix-list production and the issue-worker invocation are",
    "  left to the human PR author. Record the reason in the Phase 4.5",
    "  sticky summary as `delegation skipped: human-authored PR`.",
    "",
    "Skip the entire delegation flow below when the invocation guard",
    "denies delegation. The pushback gate, the AC/CI evaluation, and the",
    "Phase 4.5 sticky update still run.",
    "",
    "### Pushback gate (runs before every merge decision)",
    "",
    "Before taking any merge-enabling action in this phase, verify that no",
    "**unresolved `thinking_face` reactions by the reviewer** remain on",
    "any comment gathered in Phase 2.5.",
    "",
    "A `thinking_face` reaction is considered **unresolved** when:",
    "",
    "- The reviewer applied it on this pass or a prior pass, and",
    "- The underlying comment has not since received a resolution signal",
    "  from the human — for example, the comment has not been withdrawn,",
    "  the human has not posted a clarifying reply that the reviewer has",
    "  since marked `+1`, and no `review:auto-ok` label has been applied",
    "  to the PR as an explicit override.",
    "",
    "If any unresolved `thinking_face` reaction exists, **do not enable",
    "auto-merge** and **do not apply `review:awaiting-human` as a success",
    "hand-off**. This gate fires regardless of the review mode decided in",
    "Phase 2.75 — pushback blocks the merge pathway even when the mode is",
    "`auto-merge`. Skip straight to Phase 4.5 to update the sticky summary",
    "with the outstanding pushbacks, then stop. A downstream issue",
    "refines this gate further; for now, pushback simply blocks the",
    "merge pathway.",
    "",
    "If no unresolved `thinking_face` reactions exist, proceed with the",
    "branches below based on the review mode decided in Phase 2.75.",
    "",
    "### Delegation flow (in-scope fixes)",
    "",
    "When the invocation guard above permits delegation **and** Phase 3.5",
    "queued one or more `in-scope` items, the reviewer hands the fix work",
    "off to `issue-worker` via a **pull-based** marker. The reviewer never",
    "edits the PR's branch itself and never spawns `issue-worker` in",
    "session — it posts the fix-list, applies the `review:needs-worker`",
    "marker, holds the `review:fixing` lease across sessions, and exits.",
    "A top-level `issue-worker` session pulls the work; a later reviewer",
    "pass confirms it via the **Shared Procedure: Confirm a Completed",
    "Delegation**.",
    "",
    "Run steps (a) through (e) below in order. They apply in **both**",
    "`auto-merge` and `human-required` review modes — the mode-specific",
    "auto-merge decision is deferred to the confirm pass, which only",
    "re-enables auto-merge in `auto-merge` mode (in `human-required` mode",
    "the confirm pass leaves `review:awaiting-human` applied and lets the",
    "human own the final merge).",
    "",
    "#### a. Handle AC drift first",
    "",
    "For every queued `in-scope` item, re-check whether the comment is",
    "actually a request to **change the linked issue's acceptance",
    "criteria** rather than to change code that already meets the AC. If",
    "it is AC drift, do **not** delegate the item:",
    "",
    "1. Reply on the PR thread the comment came from with the exact text:",
    "   `This belongs in #<issue> — please update the issue's acceptance criteria first.`",
    "2. Post a second comment on the **linked issue** with a concrete",
    "   suggested edit to its Acceptance Criteria section that captures",
    "   what the PR comment is asking for.",
    "3. React `thinking_face` (`content=confused`) on the original PR",
    "   comment. AC-drift items count as pushbacks and the standing",
    "   pushback gate above will block any later auto-merge re-enable.",
    "4. Drop the item from the delegation fix-list — only non-AC-drift",
    "   in-scope items proceed to step (b) onwards.",
    "",
    "```bash",
    "# Reply on the PR thread",
    'gh pr comment <pr-number> --body "This belongs in #<issue> — please update the issue\'s acceptance criteria first."',
    "",
    "# Suggest the AC edit on the linked issue",
    "gh issue comment <issue-number> --body '<concrete suggested AC edit>'",
    "",
    "# React thinking_face on the PR comment (treats as pushback)",
    "gh api repos/{{repository.owner}}/{{repository.name}}/issues/comments/<comment-id>/reactions \\",
    "  -X POST -f content=confused",
    "```",
    "",
    "If **every** queued in-scope item is AC drift, the delegation",
    "fix-list is empty — skip to Phase 4.5 to record the state and stop.",
    "Do not invoke issue-worker with an empty list.",
    "",
    "#### b. Disable auto-merge",
    "",
    "Before mutating the PR, disable any pending auto-merge so a fast CI",
    "pass cannot land the PR mid-delegation. The command is idempotent —",
    "running it when auto-merge was never enabled is a safe no-op.",
    "",
    "```bash",
    "gh pr merge <pr-number> --disable-auto",
    "```",
    "",
    "#### c. Acquire the `review:fixing` lease",
    "",
    "Add the `review:fixing` label to the PR. The label is a mutual",
    "exclusion lease — only one delegation may be in flight against a",
    "given PR at a time:",
    "",
    "```bash",
    "gh pr edit <pr-number> --add-label 'review:fixing'",
    "```",
    "",
    "Before adding the label, list the PR's existing labels. If",
    "`review:fixing` is **already present**, another delegation is",
    "in-flight (or a previous delegation crashed without releasing the",
    "lease). Stop immediately, log the contention to the sticky summary,",
    "and exit. Do not invoke issue-worker, do not write a fix-list, and do",
    "not remove the existing label — a human must investigate and clear",
    "the lease manually.",
    "",
    "#### d. Write the fix-list comment",
    "",
    "Post a single PR-level comment whose body matches the format the",
    "issue-worker feedback mode parses. The comment must contain a human",
    "checkbox summary followed by a fenced ```json fix-list``` block. The",
    "JSON block is the **authoritative** list of work — the checkbox",
    "summary is for humans only.",
    "",
    "```markdown",
    "## Reviewer: fix list for @issue-worker",
    "",
    "- [ ] @<author> — <instruction summary> (<file>:<line>)",
    "",
    "```json fix-list",
    "{",
    '  "pr": <pr-number>,',
    '  "branch": "<head-ref-name>",',
    '  "generated_at": "<ISO-8601 timestamp>",',
    '  "items": [',
    '    {"comment_id": "<id>", "author": "<login>", "file": "<path>", "line": <n>, "instruction": "<imperative instruction>"}',
    "  ]",
    "}",
    "```",
    "```",
    "",
    "Each `items[]` entry corresponds to one queued in-scope comment that",
    "survived AC-drift handling. Preserve `comment_id` exactly as returned",
    "by the GitHub API in Phase 2.5 — issue-worker reports back using the",
    "same id so the reviewer can react on the correct source comment in",
    "step (f).",
    "",
    "Post the comment via `gh pr comment <pr-number> --body-file <path>`",
    "(or `--body` if the body is short). Capture the resulting comment id",
    "in memory so Phase 4.5 can cite the fix-list URL in the sticky.",
    "",
    "#### e. Apply the `review:needs-worker` marker and exit `delegated`",
    "",
    "Hand the fix work off to `issue-worker` **by marker, not by spawn**.",
    "Do **not** invoke `issue-worker` via the `Agent` tool from this pass:",
    "the reviewer frequently runs at depth-1 (the orchestrator's PR sweep",
    "dispatches it as a sub-agent), and the runtime forbids a depth-1",
    "sub-agent from spawning a depth-2 sub-agent — an in-session dispatch",
    "would silently abort and the fix-list would never be consumed.",
    "Instead, apply the durable `review:needs-worker` marker so a",
    "top-level `issue-worker` session discovers the PR and pulls the",
    "fix-list:",
    "",
    "```bash",
    "gh pr edit <pr-number> --add-label 'review:needs-worker'",
    "```",
    "",
    "Then **exit this pass returning `delegated`**, holding the",
    "`review:fixing` lease across sessions. Do **not** wait for the",
    "worker, do **not** process a report, and do **not** re-enable",
    "auto-merge in this pass. The worker applies the fix-list in its own",
    "top-level session, pushes, writes a structured worker-report comment,",
    "and clears `review:needs-worker`. A **subsequent** reviewer pass",
    "detects the lease-without-marker state and runs the **Shared",
    "Procedure: Confirm a Completed Delegation** below — that is where the",
    "`rocket` / `thinking_face` reactions, the AC/CI re-evaluation, the",
    "auto-merge re-enablement, and the `review:fixing` release happen.",
    "",
    "Before exiting, fall through to Phase 4.5 to update the sticky",
    "`## Reviewer notes` comment with the delegation state. Cite at",
    "minimum:",
    "",
    "- The fix-list comment URL produced in step (d).",
    "- That the PR now carries `review:needs-worker` and is awaiting a",
    "  top-level `issue-worker` pass.",
    "- That `review:fixing` is held across sessions until a confirm pass",
    "  releases it.",
    "- The next-step: a top-level `issue-worker` run pulls the fix-list,",
    "  then a follow-up reviewer pass confirms.",
    "",
    "Per-item `handled` / `failed` outcomes, the pushed commit SHAs, and",
    "the auto-merge decision are recorded on the **confirm pass**, not",
    "here — this pass has not yet seen the worker's result.",
    "",
    "### If all acceptance criteria are met and CI is green",
    "",
    "#### Pre-merge closing-keyword check",
    "",
    "Run this **before** branching on the review mode — it applies in both",
    "modes and is the last cheap moment to catch a body that would strand",
    "issues on merge:",
    "",
    "```bash",
    ".claude/procedures/check-closing-keywords.sh <pr-number>",
    "```",
    "",
    "Re-run it here rather than reusing the Phase 1.5 result: the body may",
    "have been edited since, and the merge is about to make the answer",
    "permanent.",
    "",
    "- **Exit `0`** — nothing to do; every linked issue will auto-close.",
    "- **Exit `1`** — the body strands at least one issue. Do **not** block",
    "  the merge; post one comment naming the stranded issues and the",
    "  corrected block, then continue. Phase 5 closes the survivors",
    "  explicitly, so the malformed body costs a comment, not a wedged PR.",
    "",
    "```bash",
    "gh pr comment <pr-number> --body 'GitHub honours only one issue reference per closing keyword, so this body would close #<first> and silently leave #<stranded…> open. Give each issue its own keyword, one per line:",
    "",
    "Closes #<first>",
    "Closes #<stranded>",
    "",
    "Merging anyway — Phase 5 will close the stranded issues explicitly.'",
    "```",
    "",
    "Carry the full stranded set into Phase 5 verbatim.",
    "",
    "Then branch on the **review mode** decided in Phase 2.75:",
    "",
    "#### Mode `auto-merge`",
    "",
    "Enable squash auto-merge with branch deletion. This queues the merge to",
    "happen once required checks pass; no separate approval review is needed.",
    "",
    "```bash",
    "gh pr merge <pr-number> --auto --squash --delete-branch \\",
    "  --subject '<conventional-commit-title>' \\",
    "  --body '<extended-description>'",
    "```",
    "",
    "The squash-merge subject should follow conventional commit format (e.g.",
    "`feat(scope): description`). The body should bullet the changes and end",
    "with `Closes #<issue-number>`.",
    "",
    "##### Update the branch when `mergeStateStatus` is `BEHIND`",
    "",
    "After enabling auto-merge, re-read the PR's `mergeStateStatus` so a",
    "branch that fell behind the default branch lands on the next CI tick",
    "instead of sitting in the auto-merge queue indefinitely:",
    "",
    "```bash",
    "gh pr view <pr-number> --json mergeStateStatus --jq '.mergeStateStatus'",
    "```",
    "",
    "Before running `gh pr update-branch`, evaluate the **eligibility",
    "gate** below. The step runs when `mergeStateStatus == BEHIND` **AND**",
    "either of the following holds:",
    "",
    "- The review mode decided in Phase 2.75 is `auto-merge`, **or**",
    "- The review mode is `human-required` **and** `matched_rule == 6`",
    "  (size threshold is the sole trigger that fixed the mode).",
    "",
    "All other `human-required` matches (rules 2–5) continue to block",
    "`update-branch`. Concretely, **never** run `gh pr update-branch`",
    "when the mode is `human-required` and any of the following fired:",
    "",
    "- rule 2 (`review:human-required` label),",
    "- rule 3 (any `labels-that-force-human` label),",
    "- rule 4 (`human-required.paths` glob match), or",
    "- rule 5 (`human-required.issue-types` match).",
    "",
    "The rationale: rule 6 fires on the **volume** of the diff alone —",
    "there is nothing about the changed paths or labels that suggests a",
    "human reviewer has explicit ownership of the branch's lifecycle.",
    "Pushing the default branch into a size-tripped human-required PR",
    "keeps it from sitting stale while the human is still drafting",
    "their review. By contrast, each of rules 2–5 signals a human",
    "reviewer who owns the branch's lifecycle; silently pushing main",
    "into those PRs expands the diff under review without their consent.",
    "",
    "When the gate **denies** `update-branch` (`human-required` mode and",
    "`matched_rule` in 2–5), record",
    "`Branch updated: not eligible (human-required by rule <N>)` and skip",
    "the rest of this sub-section. The human reviewer keeps branch-",
    "lifecycle ownership.",
    "",
    "When the gate **permits** `update-branch`, attempt to bring the head",
    "branch current with the default branch via `gh pr update-branch`",
    "(default merge strategy — **never** `--rebase`, which would rewrite",
    "commits on a published branch):",
    "",
    "```bash",
    "gh pr update-branch <pr-number>",
    "```",
    "",
    "Branch on the outcome:",
    "",
    "- **Success** — record `Branch updated: yes` for the per-PR report and",
    "  stop. Auto-merge (when enabled) will fire when required checks pass",
    "  on the new head SHA. Do **not** poll for the merge here — Phase 5",
    "  owns polling.",
    "- **Failure for reasons other than a merge conflict** (permission",
    "  denied, branch protection refusing the merge commit, transient",
    "  network error) — record `Branch updated: failed (<reason>)`, post a",
    "  short comment explaining the failure, and stop. Do not retry.",
    "- **Failure because the merge would conflict** — proceed to the",
    "  conflict-resolution delegation flow below.",
    "",
    "When `mergeStateStatus` is **not** `BEHIND` (`CLEAN`, `BLOCKED`,",
    "`UNSTABLE`, `HAS_HOOKS`, `UNKNOWN`), record `Branch updated: not needed`",
    "and skip the rest of this sub-section. Only the `BEHIND` state triggers",
    "an `update-branch` attempt — every other state either has nothing to do",
    "or is already gated on a different signal that Phase 5 picks up.",
    "",
    "##### Conflict-resolution delegation (BEHIND + conflicts)",
    "",
    "When `gh pr update-branch <pr-number>` fails because the merge would",
    "produce conflicts, hand off to the **Shared Procedure:",
    "Conflict-Resolution Delegation** section below. That procedure owns",
    "the guards, the `shared-index` vs `generic` classification, the two",
    "typed recipes, and the fallback — defined once and shared with the",
    "Phase 1.5 already-`CONFLICTING` entry point. Invoke it with this",
    "entry point's parameters:",
    "",
    "- **Entry point:** `update-branch-conflict`. The set of conflicting",
    "  files is the list reported by the failed `gh pr update-branch`",
    "  (cross-referenced with `gh pr view <pr-number> --json files`).",
    "- **On the procedure returning `delegated`:** record",
    "  `Branch updated: delegated (PR #<n>)` for the per-PR report.",
    "- **On the procedure returning `skipped` (a guard denied delegation",
    "  or the guards failed):** the procedure has already posted the",
    "  fallback comment and recorded",
    "  `Branch updated: failed (conflicts; <short reason>)`. Stop.",
    "",
    "#### Mode `human-required`",
    "",
    "Do **not** run `gh pr merge --auto`. Instead, hand the PR off to a",
    "human reviewer:",
    "",
    "1. Apply the `review:awaiting-human` label so the PR is discoverable",
    "   in the human-review queue:",
    "",
    "   ```bash",
    "   gh pr edit <pr-number> --add-label 'review:awaiting-human'",
    "   ```",
    "",
    "2. **If `matched_rule == 6`** (size threshold was the sole trigger),",
    "   run the `Update the branch when `mergeStateStatus` is `BEHIND``",
    "   step from the `Mode auto-merge` branch above before exiting. The",
    "   eligibility gate documented in that sub-section explicitly permits",
    "   `gh pr update-branch` on size-only human-required PRs so the bot",
    "   keeps the branch fresh against the default branch while the human",
    "   reviews. Skip this sub-step for `matched_rule` in 2–5 — the gate",
    "   denies `update-branch` there and the human owns branch-lifecycle.",
    "",
    "3. Exit cleanly after the acceptance-criteria check completes and any",
    "   summary comment the reviewer posts. Proceed to Phase 5 only if a",
    "   merge occurred — in `human-required` mode the reviewer stops at",
    "   the hand-off and does not poll for merge.",
    "",
    "The reason captured in Phase 2.75 should be included in any summary",
    "comment so maintainers know why human review was required (path hit,",
    "issue type, size threshold, label, or default).",
    "",
    "### If any criterion is missing, partial, or CI is failing",
    "",
    "Post a plain comment (not a formal review block) with grouped findings:",
    "",
    "```bash",
    "gh pr comment <pr-number> --body '<grouped findings>'",
    "```",
    "",
    "Group findings by severity (**Blocking** / **Suggested** / **Nitpick**).",
    "For each blocking finding, cite the unmet acceptance criterion and the",
    "file or function the gap lives in. Do **not** merge, and do **not** push",
    "any commits to the PR's branch.",
    "",
    "Rationale for using a plain comment rather than `gh pr review",
    "--request-changes`: it is lighter-weight, doesn't require the author to",
    "dismiss a formal review, and composes cleanly with the multi-PR loop.",
    "",
    "## Phase 4.5: Update Sticky Summary",
    "",
    "On **every pass**, create or update a single **`## Reviewer notes`**",
    "comment on the PR. This sticky comment is the human-facing single",
    "source of truth for the PR's state — one comment per PR, edited in",
    "place across passes, never duplicated. Do not post a fresh",
    '"pass N summary" on each iteration.',
    "",
    "### Step 1: Find the existing sticky comment (if any)",
    "",
    "List PR-level comments and look for one authored by the reviewer",
    "whose body starts with `## Reviewer notes`:",
    "",
    "```bash",
    "gh api repos/{{repository.owner}}/{{repository.name}}/issues/<pr-number>/comments",
    "```",
    "",
    "If multiple candidates exist (an older run double-posted before this",
    "phase existed), keep the earliest and plan to delete the duplicates",
    "on a later pass. Never delete another author's comment.",
    "",
    "### Step 2: Compose the sticky body",
    "",
    "The body uses the following shape:",
    "",
    "```",
    "## Reviewer notes",
    "",
    "**Mode:** auto-merge | human-required — <reason from Phase 2.75>",
    "**AC status:** <met / partial / missing> (evidence links)",
    "**CI status:** green | pending | red",
    "**Outstanding:** <list of comments still open with their classification and author>",
    "**Pushbacks:** <list of disputes with reasons>",
    "**Label reconciliation:** <consistent | corrections applied this pass>",
    "**Closing keywords:** ok | malformed — strands #<n>[, #<n>…]",
    "**Last pass:** <ISO timestamp>",
    "```",
    "",
    "Populate each field from the phases above:",
    "",
    "- **Mode / reason** — the mode and reason recorded in Phase 2.75.",
    "- **AC status** — the checklist produced in Phase 3 (met, partial,",
    "  or missing), with links to the files or tests that provide the",
    "  evidence.",
    "- **CI status** — the verdict from the **CI Verification** section",
    "  (primary check-runs read, or the Actions-runs fallback when",
    "  check-runs returns a fine-grained-PAT 403).",
    "- **Outstanding** — the comments still carrying a non-terminal",
    "  reviewer reaction from Phase 3.5 (typically `eyes` for queued",
    "  in-scope items and `nit` / `question` items that remain open).",
    "  List each as `<classification>: <author> — <short summary> (<url>)`.",
    "- **Pushbacks** — every comment the reviewer reacted",
    "  `thinking_face` to on this or any prior pass, with the reason",
    "  captured in the pushback reply. Empty list when there are none.",
    "- **Label reconciliation** — the outcome of Phase 2.8: either",
    "  `consistent` (no mutually-exclusive labels found) or a list of",
    "  the corrections applied this pass (the detected violation combo,",
    "  the label(s) removed, and the resulting self-consistent set).",
    "  Every reconciliation correction is recorded here for auditability.",
    "- **Closing keywords** — the",
    "  `.claude/procedures/check-closing-keywords.sh` verdict: `ok`, or",
    "  `malformed` plus every `STRANDED #<n>` the body would leave open.",
    "  Recorded on every pass so a body that strands issues is visible to a",
    "  human before the merge, not only after Phase 5 repairs it.",
    "- **Last pass** — the ISO 8601 timestamp of this run.",
    "",
    "### Step 3: Create or edit in place",
    "",
    "If no existing sticky comment was found in Step 1, create one:",
    "",
    "```bash",
    "gh pr comment <pr-number> --body '<sticky body>'",
    "```",
    "",
    "If an existing sticky comment was found, edit it in place using the",
    "comment id. Do not delete and re-create — editing preserves the",
    "comment's URL and any reactions humans have added to the summary:",
    "",
    "```bash",
    "gh api repos/{{repository.owner}}/{{repository.name}}/issues/comments/<sticky-comment-id> \\",
    "  -X PATCH -f body='<sticky body>'",
    "```",
    "",
    "The sticky summary must be updated on every pass through Phase 4.5,",
    "including passes that ended in a pushback-gated skip, a",
    "`NEEDS_CHANGES` findings comment, or the `human-required` hand-off.",
    "Humans rely on the sticky comment to see the current state of the",
    "PR at a glance — it must never go stale while the reviewer is",
    "actively processing the PR.",
    "",
    "## Shared Procedure: Conflict-Resolution Delegation",
    "",
    "This procedure resolves a PR that cannot land because its head",
    "branch conflicts with the default branch. It is invoked from **two**",
    "entry points, both of which reuse the identical guards,",
    "classification, typed recipes, and fallback defined here — the flow",
    "is defined **once** and shared:",
    "",
    "- **Phase 4 `update-branch-conflict`** — a PR that was MERGEABLE at",
    "  Phase 1.5 went BEHIND, and `gh pr update-branch` then failed",
    "  because the merge would conflict.",
    "- **Phase 1.5 `already-conflicting`** — the orchestrator handed the",
    "  reviewer a PR that is **already** `CONFLICTING` (its",
    '  `mergeable == "CONFLICTING"`) before any review work began.',
    "",
    "The reviewer **may** delegate conflict resolution to `issue-worker`",
    "via the feedback-mode delegation contract (the same path Phase 4's",
    "in-scope-fix delegation uses). The reviewer never hand-resolves",
    "conflicts itself — branch mutations always belong to `issue-worker`.",
    "",
    "### Entry-point parameters",
    "",
    "The caller supplies which entry point it is and how the conflicting",
    "files are identified. Everything after this step is identical across",
    "both entry points.",
    "",
    "- **`update-branch-conflict`** — the conflicting-file set is the",
    "  list reported by the failed `gh pr update-branch` (cross-referenced",
    "  with `gh pr view <pr-number> --json files`).",
    "- **`already-conflicting`** — there is no failed `update-branch` to",
    "  read conflicting paths from. Identify the conflicting files from",
    "  the PR's merge state instead: attempt a local merge of the default",
    "  branch into the head branch in a scratch checkout (or read the",
    "  conflicting paths git reports) and collect the files that carry",
    "  `<<<<<<<` / `=======` / `>>>>>>>` conflict markers. Use that set",
    "  as the conflicting-file list. The reviewer inspects these markers",
    "  read-only to classify; it does **not** stage, commit, or push any",
    "  resolution — the actual merge-and-resolve is delegated to",
    "  `issue-worker` exactly as in the other entry point.",
    "",
    "### Guards",
    "",
    "Delegate **only when all** of the following hold. If any guard fails,",
    "fall through to the **Fallback** at the end of this procedure and",
    "return `skipped`.",
    "",
    "1. **Review mode is `auto-merge`.** Never delegate conflict",
    "   resolution on `human-required` PRs — pushing worker-resolved",
    "   merge content into them expands the diff under review without",
    "   the human reviewer's consent. (Unlike the `update-branch` step,",
    "   which permits a size-only `human-required` carve-out, the",
    "   conflict-resolution delegation flow is auto-merge-only across",
    "   the board: a worker's conflict-resolution merge reconciles both",
    "   sides of each conflict into the branch, a stronger change to the",
    "   reviewed diff than the clean merge-commit `gh pr update-branch`",
    "   performs on a no-conflict branch.)",
    "2. **Delegation invocation guard permits the hand-off** — the PR",
    `   carries the \`${ORIGIN_ISSUE_WORKER_LABEL}\` label or the`,
    `   \`${ORIGIN_AGENT_LABEL}\` label, **or** the reviewer was`,
    "   invoked with `--allow-human-author`. The same guard used for the",
    "   in-scope-fix delegation flow applies here unchanged — both",
    "   agent-origin labels are delegation-eligible.",
    "3. **The `review:fixing` lease is currently free.** If",
    "   `review:fixing` is already on the PR, another delegation is",
    "   in-flight; skip conflict-resolution delegation, log the contention",
    "   to the sticky summary, and fall through to the fallback.",
    "4. **No conflicting file is generated or projen-managed.** Inspect",
    "   the conflicting paths from the entry-point step above. If **any**",
    "   conflicting path matches one of the following globs, the conflict",
    "   is unsafe to resolve mechanically and the reviewer must skip",
    "   delegation:",
    "    - `**/*.lock` and `pnpm-lock.yaml` / `yarn.lock` / `package-lock.json`",
    "    - `**/.projen/**`",
    "    - any file whose first 5 lines contain the marker",
    "      `~~ Generated by projen` or `// Generated by`.",
    "   The lockfile guard exists because lockfile conflicts often need a",
    "   regen (`pnpm i`) rather than a textual merge; the projen / generated",
    "   guard exists because those files are derived from `.projenrc.ts`",
    "   and conflicts there should be resolved by re-running synth, not by",
    "   merging the conflict markers.",
    "",
    "### Classify each conflicting file",
    "",
    "When every guard above passes, **classify each conflicting file**",
    "before composing the fix-list. The classification picks one of two",
    "typed recipes — a precise `shared-index` resolver when every",
    "conflict is a row-insert race on a registry / index / feature-matrix",
    "file, or the generic conflict-resolution recipe in every other case.",
    "",
    "**Classification step.** For each conflicting path, decide whether",
    "the file is `shared-index` or `generic` against these criteria (the",
    "shared-index glob set comes from the `shared-editing-safety` rule —",
    "see the bundle's `shared-editing.ts` for the canonical constant):",
    "",
    "- `shared-index` — the path matches one of the shared-editing glob",
    "  patterns:",
    ...DEFAULT_SHARED_INDEX_PATHS.map((p) => `    - \`${p}\``),
    "  **AND** the conflict diff is bounded to row insertions only.",
    "  Inspect the merge conflict diff for the file: every `<<<<<<<` /",
    "  `=======` / `>>>>>>>` hunk must touch only data rows (lines that",
    "  start with `| ` and are not the table header or the `|---|---|`",
    "  separator). The conflict must **not** touch:",
    "    - The frontmatter block (lines between the opening and",
    "      closing `---` fences at the top of the file).",
    "    - The page H1 or any surrounding prose paragraphs.",
    "    - The table header row (the first `| Column | ... |` row of",
    "      any table).",
    "    - The separator row (`|---|---|...|`).",
    "  If the conflict hunks touch any of the above, classify the file",
    "  as `generic` — the mechanical row-insert recipe cannot safely",
    "  reconcile structural edits.",
    "- `generic` — anything else (path outside the shared-editing glob",
    "  set, **or** conflict hunks touch the frontmatter / header /",
    "  separator / surrounding prose).",
    "",
    "**Branching emit step.**",
    "",
    "- **Every conflicting file is `shared-index`** — emit the typed",
    '  fix-list item with `comment_id: "synthetic:rebase-shared-index"`',
    "  carrying the explicit re-insert recipe (see step 3 below). The",
    "  worker mechanically re-inserts each row in declared sort order",
    "  against the merged file.",
    "- **Any conflicting file is `generic`** — emit the existing generic",
    '  fix-list item with `comment_id: "synthetic:rebase-behind-main"`',
    "  (see step 3 below). The worker performs a hand-merge of the",
    "  conflict markers.",
    "",
    "**Marker name vs. operation.** The two `comment_id` values retain",
    "their historical `rebase` spelling as a **stable cross-session",
    "identifier** — a fix-list posted by an older reviewer and consumed",
    "by a newer worker still matches. The operation they now select is a",
    "**merge** of the default branch into the PR branch, **not** a",
    "rebase: the PR branch is already pushed and diverged, so a rebase",
    "would rewrite its published history and force the worker into a",
    "`--force-with-lease` push that the never-force-push rule forbids. A",
    "merge commit is fast-forward-pushable with a plain `git push`, so",
    "the recipe stays publishable.",
    "",
    "### Hand off to `issue-worker`",
    "",
    "Hand off to `issue-worker` with the chosen single synthetic",
    "fix-list item:",
    "",
    "1. **Disable auto-merge** so a fast CI pass cannot land the PR",
    "   mid-delegation (idempotent — safe no-op when auto-merge was never",
    "   enabled). This mirrors step (b) of the in-scope-fix flow:",
    "",
    "   ```bash",
    "   gh pr merge <pr-number> --disable-auto",
    "   ```",
    "",
    "2. **Acquire the `review:fixing` lease** by adding the label",
    "   (mirrors step (c) of the in-scope-fix flow). On contention, abort",
    "   the delegation and fall through to the fallback:",
    "",
    "   ```bash",
    "   gh pr edit <pr-number> --add-label 'review:fixing'",
    "   ```",
    "",
    "3. **Post a fix-list comment** containing exactly one synthetic item",
    "   describing the conflict resolution. The `comment_id` field selects",
    "   the typed recipe (both perform a **merge** of the default branch",
    "   into the PR branch — see the marker-name note above):",
    "",
    "   - `synthetic:rebase-behind-main` — generic conflict; the worker",
    "     resolves conflicting hunks by hand.",
    "   - `synthetic:rebase-shared-index` — every conflict is a pure",
    "     row-insert race on a shared-index file; the worker re-inserts",
    "     each row in declared sort order against the merged file.",
    "",
    "   The next reviewer pass identifies the item by its `comment_id`.",
    "",
    "   **Generic shape (`synthetic:rebase-behind-main`):**",
    "",
    "   ```markdown",
    "   ## Reviewer: fix list for @issue-worker",
    "",
    "   - [ ] @reviewer — merge origin/<default-branch> into the PR branch and resolve conflicts in: <space-separated list of conflicting files>",
    "",
    "   ```json fix-list",
    "   {",
    '     "pr": <pr-number>,',
    '     "branch": "<head-ref-name>",',
    '     "generated_at": "<ISO-8601 timestamp>",',
    '     "items": [',
    '       {"comment_id": "synthetic:rebase-behind-main", "author": "reviewer", "file": "<first-conflicting-file>", "instruction": "Branch is BEHIND default-branch with merge conflicts. Fetch and merge the default branch into the PR branch: `git fetch origin && git merge --no-edit origin/<default-branch>`. Resolve the conflicts in <conflicting-files> by reconciling both sides (`<<<<<<< HEAD` is this PR\'s change), `git add` each resolved file, then `git commit --no-edit` to complete the merge commit. Push the merged branch with a plain non-force `git push origin <branch>` — the merge commit is fast-forward-pushable, so no force-push is needed — and report success."}',
    "     ]",
    "   }",
    "   ```",
    "   ```",
    "",
    "   **Typed shape (`synthetic:rebase-shared-index`).** The",
    "   `instruction` field carries the full re-insert recipe — the",
    "   worker reads it imperatively, so spell every step out:",
    "",
    "   ```markdown",
    "   ## Reviewer: fix list for @issue-worker",
    "",
    "   - [ ] @reviewer — merge origin/<default-branch> into the PR branch and re-insert shared-index rows in: <space-separated list of conflicting files>",
    "",
    "   ```json fix-list",
    "   {",
    '     "pr": <pr-number>,',
    '     "branch": "<head-ref-name>",',
    '     "generated_at": "<ISO-8601 timestamp>",',
    '     "items": [',
    '       {"comment_id": "synthetic:rebase-shared-index", "author": "reviewer", "file": "<first-conflicting-file>", "instruction": "Branch is BEHIND default-branch with shared-index row-insert conflicts only. Apply this recipe: (1) Fetch and merge the default branch into the PR branch: `git fetch origin && git merge --no-edit origin/<default-branch>`. (2) For each conflicting shared-index file: the conflict has this PR\'s row on the `<<<<<<< HEAD` side and the other PR\'s row on the `>>>>>>> origin/<default-branch>` side — extract this PR\'s row and re-insert that row in declared sort order alongside the other PR\'s row using the file\'s documented sort key (alphabetical on the first column by default) so both rows survive, remove the conflict markers, stage the file with `git add <path>`. (3) Run the commit-path verification for each row: `count=$(git show :<path> | grep -Fc <row-unique-marker>) && [ \\"$count\\" = \\"1\\" ] || exit 1`. (4) Complete the merge with `git commit --no-edit` and push with a plain non-force `git push origin <branch>` — the merge commit is fast-forward-pushable, so no force-push is needed. (5) If any conflict marker touches the frontmatter, table header row, `|---|---|` separator, or surrounding prose, abort with `git merge --abort` and emit `BLOCKED <reason>` — the precondition guards from the reviewer\'s classification step were violated."}',
    "     ]",
    "   }",
    "   ```",
    "   ```",
    "",
    "4. **Apply the durable `review:needs-worker` marker** so a",
    "   subsequent (top-level) `issue-worker` pass discovers the PR and",
    "   pulls the fix-list. Do **not** spawn `issue-worker` yourself — the",
    "   reviewer often runs at depth-1 (the orchestrator's PR sweep",
    "   dispatches it as a sub-agent) where nested sub-agent spawning is",
    "   forbidden, so an in-session `Agent` dispatch would silently abort.",
    "   The hand-off is pull-based: the marker is the signal, the",
    "   `review:fixing` lease (acquired in step 2) is held **across",
    "   sessions** until the worker clears the marker and a later reviewer",
    "   pass confirms.",
    "",
    "   ```bash",
    "   gh pr edit <pr-number> --add-label 'review:needs-worker'",
    "   ```",
    "",
    "5. **Exit returning `delegated`.** The delegation is complete for",
    "   this pass: the fix-list is posted, the lease is held, and the",
    "   marker is set. Do **not** wait for the worker, do **not** process",
    "   a worker report, and do **not** re-enable auto-merge in this pass.",
    "   The worker runs in its own top-level session; a **subsequent**",
    "   reviewer pass detects the worker finished (PR carries",
    "   `review:fixing` but **not** `review:needs-worker`), reacts on the",
    "   source comment, re-evaluates AC/CI against the merged branch,",
    "   re-enables auto-merge, and releases the `review:fixing` lease. See",
    "   the **Confirm pass** section below.",
    "",
    "Return `delegated` to the caller once the marker is applied and the",
    "fix-list posted — the delegation itself is the action taken. Unlike a",
    "merge, `delegated` does **not** imply the fix landed in this pass; it",
    "means the work is durably queued for the pull-based consumer and a",
    "later confirm pass will finish the loop.",
    "",
    "### Fallback",
    "",
    "**Fallback when delegation is not permitted or guards fail.** Post a",
    "short comment explaining why the branch could not be updated",
    "automatically and stop. Do not push commits, do not force, do not",
    "retry. Record `Branch updated: failed (conflicts; <short reason>)`",
    "and return `skipped` to the caller.",
    "",
    "```bash",
    "gh pr comment <pr-number> --body 'Branch is BEHIND <default-branch> with conflicts. <short reason delegation was skipped — e.g. human-required mode, generated-file conflicts, or in-flight review:fixing lease>. A human (or the next reviewer pass after the branch is updated) will need to resolve.'",
    "```",
    "",
    "## Shared Procedure: Confirm a Completed Delegation",
    "",
    "Delegation is **pull-based and spans sessions**: the reviewer that",
    "posts a fix-list never finishes the loop in the same pass. It posts",
    "the fix-list, acquires the `review:fixing` lease, applies the durable",
    "`review:needs-worker` marker, and exits returning `delegated`. A",
    "**separate** top-level `issue-worker` session then discovers the",
    "marker, applies the fix-list (an editorial fix or a conflict-resolution",
    "merge), pushes,",
    "writes a structured worker-report comment, and **clears**",
    "`review:needs-worker` — leaving `review:fixing` in place for the",
    "reviewer to release. The confirm pass below is how a later reviewer",
    "pass closes the loop. It runs in **both** delegation flows (the",
    "Phase 4 in-scope-fix flow and the Shared Conflict-Resolution",
    "Procedure) — the marker, not the flow, is what a confirm pass keys",
    "on.",
    "",
    "### Detecting a completed delegation",
    "",
    "Early in any reviewer pass (after Phase 2 gathers labels), inspect",
    "the PR's labels for the **lease-without-marker** state:",
    "",
    "- `review:fixing` is **present** (a delegation lease is held), **and**",
    "- `review:needs-worker` is **absent** (the worker cleared its marker",
    "  on completion).",
    "",
    "When both hold, a delegation the previous pass kicked off has been",
    "consumed and pushed by the worker. Run the confirm steps below before",
    "proceeding with the rest of the pass.",
    "",
    "When **both** `review:fixing` and `review:needs-worker` are present,",
    "the delegation is still in flight (the worker has not yet picked it",
    "up or has not finished). Do **not** re-post the fix-list and do",
    "**not** re-acquire the lease — log the in-flight state to the sticky",
    "summary and exit; a later pass confirms once the worker clears the",
    "marker.",
    "",
    "### Confirm steps",
    "",
    "1. **Read the worker's report.** Find the most recent structured",
    "   worker-report comment the `issue-worker` posted on the PR (it",
    "   cites the pushed commit SHAs and a per-item `handled` / `failed`",
    "   outcome keyed by `comment_id`). Use it to drive the reactions",
    "   below — the same per-item logic the in-session report processing",
    "   used to perform.",
    "2. **React on the source comments.** For each item:",
    "   - **`handled`** — react `rocket` on the original source comment",
    "     (the fix-list comment for a synthetic conflict-resolution item)",
    "     to signal the accepted change has landed.",
    "   - **`failed`** — react `thinking_face` (`content=confused`) on the",
    "     source comment and post a reply citing the worker's failure",
    "     reason.",
    "3. **Re-evaluate AC/CI against the merged branch.** Re-run the Phase",
    "   3 acceptance-criteria comparison and the **CI Verification** check",
    "   against the **updated** head SHA the worker pushed — do not reuse",
    "   any pre-delegation result.",
    "4. **Conditionally re-enable auto-merge** under the same four",
    "   conditions as the in-scope-fix flow's step (h): mode still",
    "   `auto-merge`, all AC met against the updated diff, no unresolved",
    "   `thinking_face` pushbacks, and CI green-or-pending.",
    "5. **Release the `review:fixing` lease** — always, even when an item",
    "   failed:",
    "",
    "   ```bash",
    "   gh pr edit <pr-number> --remove-label 'review:fixing'",
    "   ```",
    "",
    "6. **Update the sticky summary** with the per-item outcomes, the",
    "   commit SHAs from the worker report, and the auto-merge decision.",
    "",
    "### Stale-lease reclaim",
    "",
    "A crashed worker must not wedge a PR forever. A `review:fixing` lease",
    "is **orphaned** when it has no `review:needs-worker` companion",
    "**and** no consuming worker is making progress — i.e. the lease is",
    "older than the stale threshold (the same in-progress threshold the",
    "maintenance sweep applies, 72h by default) with no worker-report",
    "comment newer than the fix-list. The reclaim is wired into the",
    "maintenance sweep, not a reviewer pass: the sweep **re-applies**",
    "`review:needs-worker` (re-arming the durable work marker, leaving the",
    "`review:fixing` lease untouched) and posts an audit-trail note",
    "comment, so the Phase B1 feedback drain retries the delegation",
    "autonomously — no human is needed to free the lease. The maintenance",
    "sweep also auto-reconciles the **consumed-but-uncleared** wedge (both",
    "labels present, branch HEAD advanced past the fix-list, no",
    "worker-report) by removing `review:needs-worker` so the reviewer",
    "confirm pass can merge. A lease is never silently force-released — every",
    "reconcile posts an audit-trail note comment. See the **Stale-lease",
    "reclaim** subsection of the `pr-review-feedback-protocol` rule for the",
    "full lifecycle.",
    "",
    "## Phase 5: Branch Cleanup and Issue Closure Verification",
    "",
    "Skip this phase entirely when the review mode from Phase 2.75 is",
    "`human-required` — no merge was initiated, so there is nothing to poll",
    "and no branch to clean up. Report the hand-off and stop.",
    "",
    "In `auto-merge` mode, auto-merge may not be immediate. Poll the PR",
    "state up to 10 times, waiting 30 seconds between polls:",
    "",
    "```bash",
    "gh pr view <pr-number> --json state --jq '.state'",
    "```",
    "",
    "- **`MERGED`** — clean up locally **and** verify **every** linked issue",
    "  closed:",
    "  ```bash",
    "  git checkout {{repository.defaultBranch}}",
    "  git pull origin {{repository.defaultBranch}}",
    "  git fetch --prune origin",
    "  git branch -d <branch-name> 2>/dev/null || git branch -D <branch-name> 2>/dev/null || true",
    "  ```",
    "",
    "  **Post-merge closure verification.** Iterate the **full linked-issue",
    "  set** from Phase 1.5 — the union of the `CLOSES` and `STRANDED`",
    "  numbers `check-closing-keywords.sh` reported — not just the primary",
    "  issue. This loop is the structural guard against the stranded-issue",
    "  defect class: GitHub honours only one issue reference per closing",
    "  keyword, so a body written as `Closes #A, #B` closes `#A` and leaves",
    "  `#B` open forever with no signal anywhere. Because the reviewer",
    "  already knows the full set, every survivor is closed here",
    "  deterministically instead of being discovered months later by an",
    "  audit.",
    "",
    "  For **each** issue in the set, in order:",
    "",
    "  1. Transition it to `status:done` (replaces whichever of",
    "     `status:ready-for-review` or `status:in-progress` it was carrying):",
    "  ```bash",
    "  gh issue edit <issue-number> \\",
    '    --remove-label "status:ready-for-review" \\',
    '    --remove-label "status:in-progress" \\',
    '    --add-label "status:done"',
    "  ```",
    "  2. Check its state:",
    "  ```bash",
    "  gh issue view <issue-number> --json state --jq '.state'",
    "  ```",
    "  3. If the issue is **not** `CLOSED`, close it explicitly (this covers",
    "     both a missing closing keyword and an issue stranded by a",
    "     multi-issue keyword):",
    "  ```bash",
    "  gh issue close <issue-number> --reason completed",
    "  gh issue comment <issue-number> --body 'PR #<pr-number> merged. Closing issue.'",
    "  ```",
    "  4. Once `status:done` is applied (either via the edit above or the",
    "     explicit close path), run the targeted unblock sweep so any",
    "     dependents waiting on this issue flip from `status:blocked` to",
    "     `status:ready` immediately:",
    "  ```bash",
    "  .claude/procedures/unblock-dependents.sh <issue-number>",
    "  ```",
    "",
    "  Log the sweep's output (one line per processed dependent:",
    "  `UNBLOCKED #<n>` / `STILL_BLOCKED #<n>` / `NO_DEPENDENTS #<m>`)",
    "  in the PR review summary. See the **Agent-driven unblocking**",
    "  section in `CLAUDE.md` for the full contract.",
    "",
    "  Report one `Issue #<n>: <closed-by-github | closed-explicitly>` line",
    "  per member of the set. Any issue that needed the explicit close was",
    "  stranded by the PR body — say so in the summary so the authoring",
    "  defect is visible, not just repaired.",
    "- **`CLOSED` (not merged)** — leave the branch in place; report the closure.",
    "- **Still `OPEN`** — auto-merge is pending; report and stop without deleting.",
    "",
    "---",
    "",
    "## Output Format",
    "",
    "Return a structured report:",
    "",
    "```",
    "PR #<number> — <title>",
    "Linked issue: #<issue-number>",
    "Linked issue set: #<n> [, #<n>…]   (from check-closing-keywords.sh)",
    "Closing keywords: <ok | malformed: strands #<n>[, #<n>…]>",
    "Review mode: <auto-merge | human-required>",
    "Reason: <short explanation from Phase 2.75>",
    "Verdict: AUTO_MERGE_ENABLED | AWAITING_HUMAN | DELEGATED_TO_WORKER | NEEDS_CHANGES | INELIGIBLE | BLOCKED",
    "",
    "Acceptance criteria:",
    "  [x] <criterion> — <evidence>",
    "  [~] <criterion> — partial: <gap>",
    "  [ ] <criterion> — missing",
    "",
    "Findings:",
    "  - Blocking: <items>",
    "  - Suggested: <items>",
    "  - Nitpick: <items>",
    "",
    "Action taken: <enable-auto-merge | label-awaiting-human | delegated-to-worker | confirmed-delegation | commented-on-the-pr | none>",
    "Branch updated: <yes | not needed | failed (<reason>) | delegated (PR #<n>) | n/a>",
    "Branch state: <merged | open | closed>",
    "Issue state: <closed | open>",
    "```",
    "",
    "**`DELEGATED_TO_WORKER` semantics.** This verdict means the reviewer",
    "posted a fix-list, applied the `review:needs-worker` marker, and",
    "exited holding the `review:fixing` lease — the fix-list is **durably",
    "queued** for a top-level `issue-worker` pass, but it has **not** yet",
    "been applied in this pass. `delegated` is never a terminal",
    "completion: the loop closes only when a top-level worker consumes the",
    "marker and a later reviewer confirm pass releases the lease. Likewise",
    "`Branch updated: delegated (PR #<n>)` records that a conflict",
    "resolution was queued for the worker, not that the branch is already",
    "current.",
    "",
    "---",
    "",
    "## Rules",
    "",
    "1. **One PR per session** when invoked directly (`/review-pr`). When",
    "   invoked from the multi-PR loop (`/review-prs`), process every eligible",
    "   PR in turn.",
    "2. **Never merge without a linked issue.** If the PR body has no",
    "   `Closes #N` / `Fixes #N` / `Resolves #N`, comment and stop.",
    "2a. **Never merge without verifying every linked issue closes.** Resolve",
    "    the linked-issue set with",
    "    `.claude/procedures/check-closing-keywords.sh <pr-number>`, never by",
    "    eyeballing the body. GitHub honours only one issue reference per",
    "    closing keyword, so a `STRANDED` line means that issue will stay",
    "    open after the merge unless Phase 5 closes it. Phase 5 must iterate",
    "    the **whole** set — `CLOSES` plus `STRANDED` — and explicitly close",
    "    every issue GitHub left open.",
    "3. **Never merge with failing CI.** Even if every criterion is met,",
    "   block on red checks. Resolve CI status via the **CI",
    "   Verification** section: read check-runs first, and only on a",
    "   fine-grained-PAT 403 (or an empty rollup) fall back to the",
    "   Actions runs API. A 403 is a visibility failure, **never** a",
    "   passing check — do not treat an unreadable check as green.",
    "4. **Never bypass review conventions.** Always use `--squash`, `--auto`,",
    "   and `--delete-branch` for merges. Do not force-merge.",
    "5. **Never auto-merge a `human-required` PR.** When Phase 2.75 resolves",
    "   to `human-required`, Phase 4 applies `review:awaiting-human` and",
    "   exits. Even if every acceptance criterion is met and CI is green,",
    "   `gh pr merge --auto` is forbidden in that mode.",
    `6. **Never push commits to the PR's branch yourself.** The reviewer is a decision-maker and orchestrator, not an implementer. When in-scope review comments require code changes **and** the PR carries ${DELEGATION_ELIGIBLE_ORIGIN_PHRASE} (or the invocation prompt explicitly allows human-authored PRs via \`--allow-human-author\`), delegate the fix **by marker, not by spawn**: post a fix-list comment on the PR, apply the \`review:needs-worker\` label, hold the \`review:fixing\` lease, and exit returning \`delegated\`. Do **not** invoke \`issue-worker\` via the \`Agent\` tool — the reviewer often runs at depth-1 where nested sub-agent spawning is forbidden, so an in-session dispatch would silently abort. A top-level \`issue-worker\` session pulls the marker and applies the fix; a later reviewer confirm pass releases the lease. You must never call \`git commit\`, \`git push\`, \`git rebase\`, or \`gh pr edit\` against the head branch from this agent — all branch mutations belong to \`issue-worker\`.`,
    "7. **Do not implement code.** You review, decide, and orchestrate. If",
    "   the PR needs code changes and delegation is not permitted by Rule",
    "   #6, comment and stop — do not attempt to fix it yourself.",
    "8. **In loop mode (`/review-prs`), never stop early.** If any review",
    "   fails, comment and move to the next PR. Only abort the loop on a",
    "   fatal error (e.g. `gh` auth failure, network outage).",
    "9. **Follow CLAUDE.md conventions** for all `git` and `gh` operations.",
    "10. **Follow the reaction protocol.** Use `eyes` to mark a comment",
    "    seen, `thinking_face` (`confused`) to dispute it, `+1` to accept",
    "    its request, `rocket` when the accepted change has landed, and",
    "    `-1` when the request is out-of-scope and has been tracked in a",
    "    new issue. Apply reactions on the same comment endpoint the",
    "    comment came from (`issues/comments/...` for PR-level, ",
    "    `pulls/comments/...` for inline).",
    "11. **Terminal reactions require completed actions.** `+1`, `rocket`,",
    "    and `-1` must only be applied once the corresponding action is",
    "    truly complete (respectively: the fix accepted, the fix landed,",
    "    or the new out-of-scope issue created and linked in a reply).",
    "    Never apply a terminal reaction pre-emptively; doing so falsely",
    "    signals to humans and to future reviewer passes that the item",
    "    is closed.",
    "12. **Maintain one sticky `## Reviewer notes` comment per PR.** On",
    "    each pass, find the existing sticky comment authored by the",
    "    reviewer and edit it in place with `gh api ... -X PATCH`;",
    "    create it only when none exists. Never post per-pass summary",
    "    comments alongside the sticky — the sticky is the single",
    "    human-facing source of truth for PR state.",
    "13. **Pushback blocks auto-merge.** Any unresolved `thinking_face`",
    "    reaction authored by the reviewer on any comment must prevent",
    "    Phase 4 from enabling auto-merge, regardless of the review mode",
    "    decided in Phase 2.75. The gate clears only when the human has",
    "    resolved the dispute — by withdrawing the comment, by replying",
    "    in a way that satisfies the reviewer (who then replaces the",
    "    `thinking_face` with `+1`), or by applying the `review:auto-ok`",
    "    label as an explicit override.",
    "14. **Never re-enable auto-merge while disputes, human-required",
    "    hand-offs, or an in-flight delegation are still pending.** The",
    "    auto-merge re-enable happens on the **confirm pass** (the Shared",
    "    Procedure: Confirm a Completed Delegation), never on the pass",
    "    that posts the fix-list. A confirm pass may only run",
    "    `gh pr merge --auto` when the PR carries `review:fixing` but",
    "    **not** `review:needs-worker` (the worker finished and cleared",
    "    its marker), **and** **no** unresolved `thinking_face` reactions",
    "    authored by the reviewer remain on any PR comment, **and** the",
    "    `review:awaiting-human` label is **not** present. While",
    "    `review:needs-worker` is still applied the delegation is",
    "    unconsumed and auto-merge stays disabled. Any AC-drift pushback,",
    "    any failed-fix pushback, and any human-required hand-off all keep",
    "    auto-merge disabled until the human resolves the underlying",
    "    state.",
    "15. **Restrict `gh pr update-branch` on `human-required` PRs.** The",
    "    `update-branch` step is permitted when the review mode is",
    "    `auto-merge`, **or** when the mode is `human-required` **and**",
    "    Phase 2.75's `matched_rule == 6` (size threshold was the sole",
    "    trigger). All other human-required matches (rule 2 force-human",
    "    label, rule 3 listed force-human label, rule 4 path glob, rule 5",
    "    issue type) continue to block `update-branch`: pushing main into",
    "    those PRs expands the diff under review without the human",
    "    reviewer's consent. The same restriction applies to delegating",
    "    conflict resolution to `issue-worker`: never delegate a",
    "    conflict resolution on a `human-required` PR regardless of",
    "    `matched_rule` — the typed-recipe delegation flow stays",
    "    auto-merge-only because a worker's merge injects reconciled",
    "    content into a human-required branch, expanding the reviewed",
    "    diff more than the clean merge-commit `gh pr update-branch`",
    "    performs.",
    "16. **Never delegate conflict resolution involving generated or",
    "    projen-managed files.** When `gh pr update-branch` fails on a",
    "    BEHIND PR with conflicts and any conflicting path is a lockfile,",
    "    a `**/.projen/**` file, or a file marked",
    "    `~~ Generated by projen` / `// Generated by`, the reviewer must",
    "    skip the `issue-worker` delegation and fall back to the comment",
    "    pathway. Mechanical merge of generated content is unsafe — those",
    "    files are derived from `.projenrc.ts` and must be regenerated,",
    "    not merged.",
  ].join("\n"),
};

/*******************************************************************************
 *
 * /review-pr skill — user-invocable entry point for a single PR.
 *
 ******************************************************************************/

const reviewPrSkill: AgentSkill = {
  name: "review-pr",
  description:
    "Review a single pull request against its linked issue's acceptance criteria, then enable squash auto-merge or comment with findings",
  disableModelInvocation: true,
  userInvocable: true,
  context: "fork",
  agent: "pr-reviewer",
  platforms: { cursor: { exclude: true } },
  instructions: [
    "# Review Pull Request",
    "",
    "Run a full PR review against the linked issue's acceptance criteria,",
    "then either enable squash auto-merge or post a findings comment.",
    "",
    "## Usage",
    "",
    "/review-pr <pr-number> [--allow-human-author]",
    "",
    "### Flags",
    "",
    "- **`--allow-human-author`** — opt the reviewer into processing",
    "  human-authored PRs for **this invocation only**. By default, the",
    "  reviewer only processes bot-authored PRs (those carrying",
    `  ${DELEGATION_ELIGIBLE_ORIGIN_LIST}); pass this flag to review a`,
    "  human-authored PR. The flag does not persist — subsequent",
    "  invocations return to the bot-only default.",
    "",
    "## What This Skill Does",
    "",
    "1. Runs a pre-flight eligibility filter (mergeable, CI not failing, has linked issue)",
    "2. Fetches the PR, its diff, CI status, and the linked issue",
    "3. Builds a checklist from the issue's acceptance criteria",
    "4. Compares the diff against each criterion",
    "5. Verifies PR conventions (title, closing keyword, branch name)",
    "6. Verifies CI is green",
    "7. **If all checks pass:** enables squash auto-merge (with `--delete-branch`)",
    "8. **If any check fails:** posts a findings comment via `gh pr comment`",
    "9. After merge, verifies the linked issue is closed and closes it if not",
    "10. Cleans up the local branch after merge",
    "",
    "Passing `--allow-human-author` opts the reviewer into processing",
    "human-authored PRs for this invocation only (the default scope is",
    `bot-authored PRs carrying ${DELEGATION_ELIGIBLE_ORIGIN_LIST}).`,
    "",
    "## Input",
    "",
    "Provide the PR number to review. The skill resolves the linked issue from",
    "the PR body (`Closes #N` / `Fixes #N` / `Resolves #N`).",
    "",
    "## Output",
    "",
    "A structured report covering verdict, per-criterion status, findings",
    "grouped by severity, the action taken, and the final branch / issue state.",
    "",
    "## Composability",
    "",
    "This skill is generic and can be composed with the `github-workflow`",
    "bundle. The reviewer never implements code and never pushes to the PR",
    "branch — it only reviews, decides, and orchestrates merge or comment.",
  ].join("\n"),
};

/*******************************************************************************
 *
 * /review-prs skill — loops over every eligible open PR in the repo.
 *
 ******************************************************************************/

const reviewPrsSkill: AgentSkill = {
  name: "review-prs",
  description:
    "Loop over every eligible open pull request in the repository and review each one through the full pipeline",
  disableModelInvocation: true,
  userInvocable: true,
  context: "fork",
  agent: "pr-reviewer",
  platforms: { cursor: { exclude: true } },
  instructions: [
    "# Review All Eligible Pull Requests",
    "",
    "Run the pr-reviewer pipeline over every eligible open PR in",
    "**{{repository.owner}}/{{repository.name}}**, one after another, until",
    "the eligible queue is empty.",
    "",
    "## Usage",
    "",
    "/review-prs [--allow-human-author]",
    "",
    "### Flags",
    "",
    "- **`--allow-human-author`** — opt the reviewer into processing",
    "  human-authored PRs for **this invocation only**. By default, the",
    "  loop only processes bot-authored PRs (those carrying",
    `  ${DELEGATION_ELIGIBLE_ORIGIN_LIST}); pass this flag to include`,
    "  human-authored PRs in the queue. The flag does not persist —",
    "  subsequent invocations return to the bot-only default.",
    "",
    "## What This Skill Does",
    "",
    "### Step 1: Enumerate open PRs",
    "",
    "```bash",
    "gh pr list --json number,title,body,headRefName,mergeable,mergeStateStatus,statusCheckRollup --limit 50",
    "```",
    "",
    "### Step 2: Filter to eligible PRs",
    "",
    "A PR is **eligible** when all of the following hold:",
    "",
    '1. `state == "OPEN"` (implicit from `gh pr list`).',
    '2. `mergeable == "MERGEABLE"` (no conflicts).',
    "3. No required check in `statusCheckRollup` has a failing conclusion",
    "   (`FAILURE`, `TIMED_OUT`, `CANCELLED`, `ERROR`). CI green or still",
    "   pending is fine. When `statusCheckRollup` is empty or denied with",
    "   a fine-grained-PAT 403, keep the PR in the queue — the per-PR",
    "   pipeline resolves CI authoritatively via the reviewer's CI",
    "   Verification Actions-runs fallback.",
    "4. The PR body contains a linked issue (`Closes #N` / `Fixes #N` /",
    "   `Resolves #N`, case-insensitive).",
    "",
    "Drop any PR that fails the filter from the queue. Do not comment on",
    "them from this skill — the individual `/review-pr` invocation handles",
    "the inline rejection comment when run on a specific PR.",
    "",
    "### Step 3: Process each eligible PR",
    "",
    "For every eligible PR, invoke the full pr-reviewer pipeline",
    "(Phases 1 through 5) as if `/review-pr <number>` had been called",
    "directly:",
    "",
    "- Gather context (diff, checks, linked issue)",
    "- Compare diff to acceptance criteria",
    "- Decide and act (enable auto-merge **or** post findings comment)",
    "- Verify branch / issue cleanup after merge",
    "",
    "Continue to the next PR after each one completes. Never stop the loop",
    "early because a single PR's review failed — comment and move on.",
    "",
    "### Step 4: Stop when the queue is empty",
    "",
    "When no eligible PRs remain, emit a final summary listing every PR",
    "processed and the verdict for each, then stop.",
    "",
    "Passing `--allow-human-author` opts the reviewer into including",
    "human-authored PRs in the queue for this invocation only (the default",
    `scope is bot-authored PRs carrying ${DELEGATION_ELIGIBLE_ORIGIN_LIST}).`,
    "",
    "## Output",
    "",
    "Per-PR structured report (same shape as `/review-pr`), followed by a",
    "one-line-per-PR summary at the end:",
    "",
    "```",
    "Summary:",
    "  PR #<n>: <verdict>",
    "  PR #<n>: <verdict>",
    "  ...",
    "Total processed: <count>",
    "```",
    "",
    "## Failure Handling",
    "",
    "Only abort the loop on a fatal error (e.g. `gh` authentication failure,",
    "network outage). A failed review for an individual PR is not fatal —",
    "comment on that PR and continue with the next.",
  ].join("\n"),
};

/*******************************************************************************
 *
 * Bundle definition — opt-in. Consumers must include via `includeBundles`
 * (or it auto-detects through the appliesWhen below). No hardcoded
 * domain-specific content.
 *
 ******************************************************************************/

/**
 * Build the `pr-review` bundle with the supplied (possibly absent)
 * PR review policy override.
 *
 * The bundle is mostly static — the agent prompt, the feedback-
 * protocol prose, the skills, the sub-agent, and the labels are all
 * fixed across consumers. The one dynamic surface is the rendered
 * `pr-review-policy` rule's YAML block and precedence walk, which
 * reflect the resolved `pathsExemptFromSize` carve-out so consumers
 * tuning the doc-only carve-out see their override land in the
 * rendered CLAUDE.md.
 *
 * When `policy` is omitted, the bundle ships with the documented
 * defaults baked in (`pathsExemptFromSize: ["docs/**"]`).
 */
export function buildPrReviewBundle(
  policy: ResolvedPrReviewPolicy = resolvePrReviewPolicy(),
): AgentRuleBundle {
  return {
    name: "pr-review",
    description:
      "Pull request review workflow: verifies PRs against their linked issues' acceptance criteria and orchestrates squash-merge, single or looped over all eligible PRs",

    // Default-apply: the PR review workflow is safe to include everywhere,
    // and keeping review/merge policy centralised in the pr-reviewer agent
    // means consumers get consistent behaviour out of the box. Consumers can
    // still exclude it explicitly via `excludeBundles` if desired.
    appliesWhen: () => true,

    rules: [
      {
        name: "pr-review-policy",
        description:
          "Declarative policy that tells the pr-reviewer which PRs may auto-merge and which must wait for a human reviewer",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        // Bundle defaults exclude paths into configulator's own source
        // (only meaningful when configulator is a workspace package).
        // codedrifters/packages restores them via
        // `agentConfig.additionalRulePaths`.
        filePatterns: [
          ".claude/agents/pr-reviewer.md",
          ".claude/skills/review-pr/**",
          // The operator skill quotes the policy's CI-verification
          // contract verbatim; loading the policy when it is edited
          // keeps the two from drifting.
          ".claude/skills/babysit-prs/**",
        ],
        content: [
          "# PR Review Policy",
          "",
          "The `pr-reviewer` sub-agent evaluates every PR it reviews against the",
          "policy below and routes the PR into one of two modes:",
          "",
          "- **`auto-merge`** — the reviewer may enable squash auto-merge once",
          "  all acceptance criteria are met and CI is green.",
          "- **`human-required`** — the reviewer runs the full AC/CI check but",
          "  never calls `gh pr merge --auto`. It applies the",
          "  `review:awaiting-human` label and hands off to a human reviewer.",
          "",
          "## Policy",
          "",
          "```yaml",
          "version: 1",
          "default: auto-merge",
          "",
          "human-required:",
          "  paths:",
          '    - "docs/src/content/docs/project-context.md"',
          '    - ".github/workflows/**"',
          '    - ".github/CODEOWNERS"',
          '    - ".projenrc.ts"',
          '    - "projenrc/**"',
          '    - "CLAUDE.md"',
          '    - ".claude/**"',
          '    - "packages/**/package.json"',
          "  issue-types:",
          "    - release",
          "    - hotfix",
          "  size:",
          "    files: 10",
          "    insertions: 500",
          "  labels-that-force-human:",
          '    - "review:human-required"',
          "",
          "auto-merge:",
          "  labels-that-force-auto:",
          '    - "review:auto-ok"',
          "  paths-exempt-from-size:",
          ...renderPathsExemptFromSizeYaml(
            policy.autoMerge.pathsExemptFromSize,
          ),
          "",
          "ci-verification:",
          "  required-workflows:",
          ...renderRequiredWorkflowsYaml(
            policy.ciVerification.requiredWorkflows,
          ),
          "```",
          "",
          "## Precedence",
          "",
          "The reviewer walks the following checks in order. The **first match**",
          "fixes the mode; any mixed-match PR (signals from both sides) resolves",
          "conservatively to `human-required` — `auto-merge` only wins when the",
          "force-auto label is the single top-priority match.",
          "",
          "1. **`auto-merge.labels-that-force-auto`** — if the PR carries any of",
          "   these labels (e.g. `review:auto-ok`), the mode is `auto-merge`",
          "   outright. This is the only escape hatch from the conservative",
          "   default; it requires a maintainer to apply the label explicitly.",
          "2. **`review:human-required` label** — reserved force-human label;",
          "   if present (and no force-auto label beat it in step 1), the mode",
          "   is `human-required`.",
          "3. **`human-required.labels-that-force-human`** — if the PR",
          "   carries any label listed here (e.g. `review:human-required`),",
          "   the mode is `human-required`.",
          "4. **`human-required.paths`** — if any file in the PR diff matches",
          "   any glob here, the mode is `human-required`. Matching uses",
          "   standard glob semantics (`**` for recursive directories,",
          "   `*` for a single path segment).",
          "5. **`human-required.issue-types`** — if the linked issue's GitHub",
          "   issue type matches any entry (case-insensitive), the mode is",
          "   `human-required`.",
          "6. **`human-required.size`** — first read",
          "   `auto-merge.paths-exempt-from-size` from the policy block above.",
          "   For every file in the PR diff, evaluate whether the file path",
          "   matches at least one glob in that carve-out list. If **every**",
          "   changed path matches the carve-out, **skip rule #6** entirely",
          "   and continue to rule #7 — the PR is exempt from the size",
          "   threshold regardless of its `files` or `insertions` count.",
          "   Otherwise (any changed path falls outside the carve-out list),",
          "   apply the size check: if the PR exceeds either the `files`",
          "   count or the `insertions` count, the mode is `human-required`.",
          "7. **`default`** — applied only when no rule above matched",
          "   (normally `auto-merge`).",
          "",
          "**Label-check scope.** All label checks in rules 1–3 evaluate",
          "the PR's **own** labels only. Never traverse the `Closes #N`",
          "link to read the linked issue's labels for a label rule. Rule 5",
          "is the only rule that reads the linked issue, and it reads the",
          "issue **type**, not its labels.",
          "",
          "**`priority:*` / `status:*` are not merge-risk signals.**",
          "`priority:*` and `status:*` are issue-triage labels consumed by",
          "the orchestrator for scheduling — `priority:*` sequences the",
          "work queue and `status:*` tracks workflow position. They say",
          "nothing about a PR's merge risk and must never appear in any",
          "`labels-that-force-*` list. Gating merge on `priority:critical`",
          "in particular inverts the intended behaviour: the most urgent",
          "work would wait the longest for a human merge. Merge risk is",
          "already covered by the path globs (rule 4), the",
          "`release` / `hotfix` issue-types (rule 5), and the size",
          "thresholds (rule 6).",
          "",
          "The `auto-merge.paths-exempt-from-size` carve-out exists so",
          "**doc-only PRs** that routinely exceed the 500-insertion size",
          "threshold (large migrations, bulk additions, refresh passes)",
          "are not forced into `human-required` mode for a reason that does",
          "not reflect production risk. The default carve-out exempts the",
          "entire `docs/**` tree — every consumer of configulator places its",
          "Starlight docs site there. A PR mixing docs and code still falls",
          "into `human-required` at rule #6 because the non-docs path fails",
          "the carve-out check, so the rule only relaxes the threshold for",
          "PRs whose **every** changed path is doc-only.",
          "",
          "### ADRs auto-merge as `Proposed`",
          "",
          "Architectural Decision Records (under",
          "`docs/src/content/docs/requirements/architectural-decisions/**`)",
          "are **not** a `human-required.paths` trigger. An ADR-only PR with",
          "green CI and met acceptance criteria resolves to `auto-merge` and",
          "squash-merges with no `review:awaiting-human` and no human action.",
          "",
          "Auto-merging an ADR does **not** ratify the decision it records.",
          "An ADR is authored in `Proposed` status, and merging it simply",
          "lands the proposal in the docs tree as written — its status stays",
          "`Proposed`. Promotion `Proposed` → `Accepted` is a separate",
          "human / governance step performed out of band by a",
          "decision-maker; it is **not** a merge gate and nothing in the",
          "review loop flips an ADR to `Accepted`. Routing ADRs to",
          "`human-required` only piled them up at `review:awaiting-human`",
          "waiting for a merge decision that was never the human's job —",
          "the human's job is the later acceptance decision, not the merge.",
          "",
          "The `requirements-writer` flow already authors ADRs in",
          "`Proposed` status (its Decision section reads",
          '"Pending human review"), so the merged-as-`Proposed` terminal',
          "state is supplied by the author, not enforced at the merge gate.",
          "Consumers that genuinely need a human to gate ADR *merges* (not",
          "just acceptance) cannot re-add the glob via policy config — the",
          "`human-required.paths` list is not consumer-configurable today;",
          "they would apply the `review:human-required` label per-PR or",
          "fork the bundle.",
          "",
          "The `pr-reviewer` sub-agent records the decided mode, the",
          "triggering reason, and the numeric **matched-rule index** (2–6",
          "for human-required matches; `null` for rule 1 force-auto or",
          "rule 7 default) in its Phase 2.75 output. Downstream phases and",
          "the sticky summary cite the specific rule that applied.",
          "",
          "### Rule-#6 carve-out for `gh pr update-branch`",
          "",
          "The reviewer's BEHIND-branch refresh step (`gh pr update-branch`)",
          "is normally restricted to `auto-merge` PRs because pushing the",
          "default branch into a `human-required` PR expands the diff the",
          "human is reviewing without their consent. A narrow exception",
          "applies when rule #6 (size threshold) is the **sole** trigger",
          "for `human-required` mode: the bot may still run `gh pr",
          "update-branch` so a code-heavy size-tripped PR does not sit",
          "BEHIND while the human drafts their review.",
          "",
          "The exception is keyed on the matched-rule index recorded in",
          "Phase 2.75. All other `human-required` triggers — rule 2",
          "(`review:human-required` label), rule 3 (any",
          "`labels-that-force-human` label), rule 4",
          "(`human-required.paths` glob match), and rule 5",
          "(`human-required.issue-types` match) — continue to block",
          "`update-branch` because each one signals a human reviewer who",
          "has explicit ownership of the branch's lifecycle.",
          "",
          "This carve-out is largely belt-and-suspenders given the doc-only",
          "size carve-out above. Doc-only PRs that trip rule #6 now route",
          "directly to `auto-merge`, so the rule-#6 `update-branch` carve-",
          "out only kicks in for **code-heavy** PRs that legitimately trip",
          "rule #6 (mixed-content diffs whose non-doc paths fail the",
          "`paths-exempt-from-size` check, or consumers that disable the",
          "doc-only carve-out entirely).",
          "",
          "## CI Verification",
          "",
          "Before enabling auto-merge the reviewer confirms CI is green.",
          "The **primary** read is the GitHub check-runs rollup",
          "(`gh pr checks` / `statusCheckRollup`) — the same source",
          "GitHub uses for branch protection, covering **every** check",
          "context (Actions, third-party CI, GitHub Apps, commit",
          "statuses). It stays primary for every consumer because it is",
          "strictly more complete than the Actions-only fallback below.",
          "",
          "### Fine-grained-PAT fallback to the Actions runs API",
          "",
          "When the reviewer authenticates with a **fine-grained personal",
          "access token**, the check-runs read returns",
          "`HTTP 403: Resource not accessible by personal access token`.",
          "This is not grantable away: GitHub exposes **no `Checks`",
          "permission for fine-grained tokens** (it is GitHub-App-only),",
          "so the token permission picker has no entry to add. On that",
          "specific 403 (or when the rollup returns no readable contexts",
          "at all), the reviewer falls back to the **Actions runs API**:",
          "",
          "```",
          "GET /repos/{owner}/{repo}/actions/runs?head_sha={head_sha}&per_page=100",
          "```",
          "",
          "which a fine-grained PAT with `Actions: Read-only` **can**",
          "read. Auto-merge is then gated on the required workflows'",
          "latest run per workflow name concluding `success`",
          "(`skipped` / `neutral` are non-blocking; `failure` /",
          "`cancelled` / `timed_out` / `action_required` block; a run",
          "still `in_progress` / `queued`, or a required workflow with no",
          "run at all, counts as not-yet-green). The 403 is detected",
          "explicitly and is **never** treated as a passing check — it is",
          "a visibility failure, not a green signal.",
          "",
          "The fallback fires **only** when the primary check-runs read",
          "is denied, so GitHub-App and classic-PAT consumers (which can",
          "read check-runs) are unaffected and keep the complete view.",
          "The fallback only sees GitHub Actions runs; a consumer whose",
          "CI includes a non-Actions required check cannot verify it on a",
          "fine-grained PAT and must move the reviewer to a GitHub App /",
          "classic PAT to regain the complete check-runs view.",
          "",
          "### Configuring required workflows",
          "",
          "The `ci-verification.required-workflows` list in the policy",
          "YAML names the workflows the fallback gates on:",
          "",
          "- **Non-empty list** — gate only on those workflow `name`s.",
          "  A listed workflow with no run for the head SHA is treated as",
          "  not-yet-green (pending), never green.",
          "- **Empty list (the default)** — treat **every** workflow run",
          "  observed for the head SHA as required. This is the",
          "  conservative zero-config default so an unknown failing",
          "  workflow blocks rather than slips through.",
          "",
          "Consumers tune the list via",
          "`agentConfig.prReviewPolicy.ciVerification.requiredWorkflows`.",
          "The list governs **only** the fallback path; the primary",
          'check-runs read derives "required" from branch protection',
          "directly.",
        ].join("\n"),
        tags: ["policy", "review"],
      },
      {
        name: "pr-review-workflow",
        description:
          "Describes the /review-pr and /review-prs skills and their delegation to the pr-reviewer sub-agent",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# PR Review Workflow",
          "",
          "Two skills are available, both backed by the same `pr-reviewer`",
          "sub-agent:",
          "",
          "- **`/review-pr <pr-number>`** — review a single targeted PR.",
          "- **`/review-prs`** — loop over every eligible open PR in the",
          "  repository and review each one in turn.",
          "",
          "The `pr-reviewer` sub-agent:",
          "",
          "1. Runs a pre-flight eligibility filter (mergeable, CI not failing,",
          "   has a linked issue). Ineligible PRs get a short comment and are",
          "   skipped.",
          "2. Fetches the PR, its diff, CI status, and the linked issue",
          "3. **Evaluates the PR Review Policy** (see the `PR Review Policy`",
          "   section above) to decide whether the PR is `auto-merge` or",
          "   `human-required`, and records the triggering reason",
          "4. Builds a checklist from the issue's acceptance criteria",
          "5. Verifies the diff satisfies each criterion and that CI is green",
          "6. **Enables squash auto-merge** (with `--delete-branch`) when all",
          "   checks pass **and** the review mode is `auto-merge`",
          "7. **Applies `review:awaiting-human`** and hands off to a human",
          "   reviewer when the review mode is `human-required` (no auto-merge,",
          "   even if every acceptance criterion is met)",
          "8. **Comments with grouped findings** when any check fails (plain",
          "   `gh pr comment`, not a formal `--request-changes` review)",
          "9. After a successful merge, verifies the linked issue is closed",
          "   and closes it explicitly if the merge commit did not",
          "10. Cleans up the local branch after merge",
          "",
          "The reviewer **never** implements code and **never** pushes commits",
          "to a PR's branch — it only reviews, decides, and orchestrates merge",
          "or comment. In loop mode, a failed review for one PR never stops",
          "the loop; the reviewer comments and moves on. See the `pr-reviewer`",
          "agent definition for the full phase-by-phase contract.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
      {
        name: "pr-review-feedback-protocol",
        description:
          "Documents the human-in-the-loop feedback loop on PR review: reaction state machine, pushback resolution, fix-list comment format, sticky reviewer-notes comment, label glossary, and human-author opt-in flag.",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        // Bundle defaults exclude paths into configulator's own source
        // (only meaningful when configulator is a workspace package).
        // codedrifters/packages restores them via
        // `agentConfig.additionalRulePaths`.
        filePatterns: [
          ".claude/agents/pr-reviewer.md",
          ".claude/agents/issue-worker.md",
          // The operator skill's D2 handler routes on the label states
          // this protocol defines; it must load alongside them.
          ".claude/skills/babysit-prs/**",
        ],
        content: [
          "# PR Review Feedback Protocol",
          "",
          "## Human-in-the-Loop Feedback Protocol",
          "",
          "The PR review pipeline is a **human-in-the-loop feedback loop**.",
          "Reviewers (human or agent) leave comments on the PR; the",
          "`pr-reviewer` sub-agent classifies each comment, reacts to it,",
          "delegates in-scope fixes to `issue-worker`, and updates a single",
          "sticky `## Reviewer notes` comment that is the canonical record of",
          "PR state. The sections below document the conventions humans need",
          "to read and drive that loop.",
          "",
          "### Trigger Model: Single-Pass, Pull-Based Delegation",
          "",
          "Each reviewer pass runs exactly once and does not self-chain. A",
          "human (or a scheduled PR-review sweep) re-invokes",
          "`/review-pr <n>` (or `/review-prs`) whenever the PR state changes",
          "enough to warrant another look — a new comment, a new commit, a",
          "resolved pushback, a label flip, or a worker clearing the",
          "`review:needs-worker` marker. The reviewer never reschedules",
          "itself.",
          "",
          "Delegation is **pull-based, not push-based**. The reviewer does",
          "**not** spawn `issue-worker` in session — it frequently runs at",
          "depth-1 (the orchestrator's PR sweep dispatches it as a",
          "sub-agent), and the runtime forbids a depth-1 sub-agent from",
          "spawning a depth-2 sub-agent, so an in-session dispatch would",
          "silently abort. Instead the pass that decides to delegate posts a",
          "fix-list, applies the durable `review:needs-worker` marker, holds",
          "the `review:fixing` lease, and exits. A **separate** top-level",
          "`issue-worker` session discovers the marker, applies the",
          "fix-list, pushes, writes a worker-report comment, and clears",
          "`review:needs-worker`. A later reviewer pass then confirms the",
          "result and releases the lease. The worker's run is therefore",
          "**not** the terminal step of the delegating reviewer pass — the",
          "loop spans three passes (delegate → worker → confirm).",
          "",
          "This keeps the loop cheap to reason about: every step is",
          "traceable to a label state on the PR, and the producer",
          "(`pr-reviewer`, at any depth) never depends on a depth-2",
          "sub-agent spawn to reach the consumer (`issue-worker`, always",
          "top-level).",
          "",
          "### Reaction State Machine",
          "",
          "The reviewer signals its disposition toward each human comment via",
          "GitHub reactions on that comment. Five reactions carry meaning in",
          "this workflow; every other reaction is ignored.",
          "",
          "| Reaction | Meaning | Terminal? |",
          "|----------|---------|-----------|",
          "| `eyes` | Seen by reviewer; no terminal decision yet. Queued for processing on this or a later pass. | No |",
          "| `+1` | Reviewer accepted the comment's request; a fix has been queued or has already landed. | **Yes** |",
          "| `rocket` | The accepted fix has landed on the branch. The reviewer's reply cites the commit SHA that applied it. | **Yes** |",
          "| `thinking_face` | Reviewer pushback — the comment conflicts with an acceptance criterion, a CLAUDE.md convention, the project-context doc, or is ambiguous. **Blocks auto-merge** until resolved. | No |",
          "| `-1` | Declined as out-of-scope. A separate tracking issue was created; the reviewer's reply links to it. | **Yes** |",
          "",
          "Terminal reactions (`+1`, `rocket`, `-1`) are applied **only after**",
          "the corresponding action has truly completed — the fix accepted,",
          "the commit landed on the branch, or the out-of-scope tracking",
          "issue created and linked in a reply. The reviewer never applies a",
          "terminal reaction pre-emptively.",
          "",
          "A comment carrying only `eyes` or `thinking_face` from the",
          "reviewer is **non-terminal** and will be re-evaluated on the next",
          "pass. A comment carrying any terminal reaction authored by the",
          "reviewer is dropped from future classification.",
          "",
          "GitHub's reactions API uses `confused` as the content string for",
          "the `thinking_face` reaction (`content=confused` when POSTing).",
          "",
          "### Resolving a Pushback",
          "",
          "When the reviewer pushes back on a comment with `thinking_face`,",
          "auto-merge is blocked until the dispute is resolved. Humans have",
          "three ways to clear a pushback:",
          "",
          "1. **Withdraw the comment.** Delete the comment, or edit out the",
          "   disputed request, then re-invoke `/review-pr <n>`. The reviewer",
          "   drops the withdrawn item from its queue on the next pass.",
          "2. **Reply with clarification.** Post a reply on the same thread",
          "   that addresses the reviewer's objection (cite the acceptance",
          "   criterion you meant, supply the missing context, or concede the",
          "   point). Re-invoke `/review-pr <n>` — the reviewer re-classifies",
          "   the thread and may promote `thinking_face` to `+1` if the",
          "   clarification satisfies it.",
          "3. **Force through with `review:auto-ok`.** Apply the",
          "   `review:auto-ok` label to the PR as an explicit maintainer",
          "   override. The reviewer will log the override in the sticky",
          "   `## Reviewer notes` comment and proceed with auto-merge even",
          "   though the dispute was never resolved by reply or withdrawal.",
          "",
          "### Fix-List Comment Format",
          "",
          "When Phase 4 delegates in-scope fixes to `issue-worker`, it posts",
          "a single PR-level comment whose body carries both a human-readable",
          "checkbox summary and a fenced ```json fix-list``` block. The JSON",
          "block is the authoritative payload the worker parses; the",
          "checkbox list is for humans reading the PR.",
          "",
          "```markdown",
          "## Reviewer: fix list for @issue-worker",
          "",
          "- [ ] @<author> — <instruction summary> (<file>:<line>)",
          "",
          "```json fix-list",
          "{",
          '  "pr": <pr-number>,',
          '  "branch": "<head-ref-name>",',
          '  "generated_at": "<ISO-8601 timestamp>",',
          '  "items": [',
          '    {"comment_id": "<id>", "author": "<login>", "file": "<path>", "line": <n>, "instruction": "<imperative instruction>"}',
          "  ]",
          "}",
          "```",
          "```",
          "",
          "Each `items[]` entry corresponds to one in-scope comment the",
          "reviewer queued on this pass. The `comment_id` is preserved",
          "exactly as returned by the GitHub API so that `issue-worker` can",
          "report per-item outcomes and the reviewer can apply `rocket` or",
          "`thinking_face` to the correct source comment on the next pass.",
          "",
          "### Cross-Session `review:fixing` Lease Lifecycle",
          "",
          "Delegation hand-off is **pull-based and spans three passes**. Two",
          "labels coordinate it: the `review:fixing` lease (mutual exclusion)",
          "and the `review:needs-worker` marker (durable work signal). The",
          "lease is held **across sessions** — it is acquired by the",
          "reviewer pass that posts the fix-list and released only by a",
          "later reviewer confirm pass, never within a single pass.",
          "",
          "| Step | Actor | Labels after the step |",
          "|------|-------|-----------------------|",
          "| **1. Delegate** | `pr-reviewer` | Posts the fix-list, **acquires** `review:fixing`, **applies** `review:needs-worker`, exits `delegated`. PR carries both labels. |",
          "| **2. Consume** | `issue-worker` (top-level) | Discovers `review:needs-worker`, applies the fix-list, pushes, writes a worker-report comment, **clears** `review:needs-worker`. PR carries `review:fixing` only. |",
          "| **3. Confirm** | `pr-reviewer` | Detects `review:fixing` **without** `review:needs-worker`, reacts on source comments from the worker report, re-evaluates AC/CI, re-enables auto-merge (auto-merge mode), **releases** `review:fixing`. PR carries neither label. |",
          "",
          "Key invariants:",
          "",
          "- The producer (`pr-reviewer`) **never spawns** the consumer",
          "  (`issue-worker`) in session. The reviewer can run at depth-1,",
          "  where nested sub-agent spawning is forbidden; the marker is the",
          "  hand-off, not an `Agent` dispatch.",
          "- The lease being held while `review:needs-worker` is still",
          "  present means the delegation is **in flight** — a reviewer pass",
          "  that sees both labels logs the in-flight state and exits without",
          "  re-posting the fix-list or re-acquiring the lease.",
          "- The lease being held **without** `review:needs-worker` is the",
          "  **confirm trigger** — exactly one delegation has been consumed",
          "  and is awaiting confirmation.",
          "- `delegated` is never a terminal completion. A PR is only done",
          "  when the confirm pass releases the lease and (in auto-merge",
          "  mode) re-enables auto-merge.",
          "",
          "#### Stale-lease reclaim",
          "",
          "A crashed or never-launched worker must not wedge a PR by holding",
          "the lease forever, and a worker that pushes but skips its hand-off",
          "must not wedge the PR either. The **maintenance sweep** (not a",
          "reviewer pass) owns reconcile for **both** stuck states — its",
          "`check-blocked.sh maintenance` invocation runs the reconcile",
          "directly and emits a `LEASE_RECONCILE orphaned=<N>",
          "consumed_uncleared=<M>` summary line. No human is needed to free a",
          "stuck PR.",
          "",
          "**Orphaned lease.** A `review:fixing` lease is **orphaned** when",
          "**both** hold:",
          "",
          "- The PR carries `review:fixing` but **not**",
          "  `review:needs-worker` — there is no in-flight worker still",
          "  expected to clear the marker, **yet**",
          "- No worker-report comment newer than the fix-list exists, **and**",
          "  the fix-list comment is older than the stale in-progress",
          "  threshold the maintenance sweep applies (72h by default).",
          "",
          "Together these mean a delegation was kicked off, the marker was",
          "consumed-or-dropped, but no worker result ever landed — the lease",
          "is orphaned. The maintenance sweep **re-applies**",
          "`review:needs-worker` (re-arming the durable work marker so the",
          "Phase B1 feedback drain retries the delegation) and posts an",
          "audit-trail note comment. The `review:fixing` lease itself is left",
          "untouched, so the drain stays idempotent. This is a deliberate",
          "re-arm for autonomy — never a silent force-release: the note",
          "comment is the audit trail proving the re-arm was the sweep's",
          "doing.",
          "",
          "**Consumed-but-uncleared wedge.** The existing orphaned-lease",
          "precondition (`review:fixing` *without* `review:needs-worker`)",
          "structurally cannot see a worker that pushed its fix and then",
          "skipped the hand-off — the marker is still set. The sweep detects",
          "this wedge when a PR carries **both** `review:needs-worker` and",
          "`review:fixing`, its branch HEAD commit is **newer** than the",
          "fix-list comment, and there is **no** worker-report. It",
          "auto-reconciles by **removing** `review:needs-worker` (the",
          "hand-off the worker skipped) and posting an audit-trail reconcile",
          "note comment, flipping the PR into the lease-without-marker",
          "confirm-trigger state so the reviewer confirm pass takes over and",
          "merges. As with the orphaned-lease path, the `review:fixing` lease",
          "is left for that confirm pass to release.",
          "",
          "### Review-Label Invariants (Phase 2.8 reconciliation)",
          "",
          "A PR must never carry **mutually-exclusive review-workflow",
          "labels** after a reviewer pass — the PR equivalent of the",
          '"exactly one `status:*`" discipline issues already follow.',
          "Successive passes layering labels with no reconciler can drive a",
          "PR into an impossible state (a real PR once held",
          "`review:awaiting-human` + `status:needs-attention` +",
          "`review:fixing` + `review:needs-worker` at the same time). The",
          "reviewer's **Phase 2.8 — Label Reconciliation** step enforces",
          "the invariants below early in every pass, after Phase 2.75",
          "decides the mode and after the confirm-trigger lease detection,",
          "but before any phase branches on review state. Every correction",
          "is logged on a `**Label reconciliation:**` line in the sticky",
          "`## Reviewer notes` comment — labels are never mutated silently.",
          "",
          "**The invariants:**",
          "",
          "1. **`review:awaiting-human` is mutually exclusive with the",
          "   fix-delegation labels (`review:needs-worker`,",
          "   `review:fixing`).** A PR is **either** in the fix-delegation",
          "   loop **or** handed to a human — never both. Any of",
          "   `review:awaiting-human` + `review:needs-worker`,",
          "   `review:awaiting-human` + `review:fixing`, or all three is a",
          "   violation.",
          "2. **`review:fixing` WITHOUT `review:needs-worker` is the",
          "   confirm-pending state, not a violation.** The worker finished",
          "   and cleared its marker; a confirm pass owns the PR and will",
          "   release the lease. The reconciler must not strip it.",
          "3. **`review:fixing` WITH `review:needs-worker` is a valid",
          "   in-flight delegation, not a violation.** The work is durably",
          "   queued for a top-level `issue-worker` pass.",
          "4. **`status:needs-attention` is additive but must not coexist",
          "   with a live, progressing in-flight delegation.** When",
          "   `review:fixing` + `review:needs-worker` are present, the",
          "   fix-list is newer than the flag, and there is no failed-fix",
          "   pushback, the flag contradicts the live progress signal and is",
          "   removed. A flag coexisting with a *stalled* / *failed*",
          "   delegation, an unresolved `thinking_face` pushback, or a",
          "   `human-required` hand-off is legitimate and stays.",
          "",
          "(`review:human-required` and `review:auto-ok` are **policy",
          "inputs** read in Phase 2.75, not workflow-state labels — the",
          "reconciler never touches them.)",
          "",
          "**Canonical resolution.** The losing label is chosen by the",
          "Phase 2.75 mode plus the current loop position so the surviving",
          "set matches the action the pass will take:",
          "",
          "| Violation combo | Loop position / mode | Resolution (winner) |",
          "|-----------------|----------------------|---------------------|",
          "| `review:awaiting-human` + fix-delegation label(s) | delegation in-flight or confirm-pending, mode `auto-merge` | **remove `review:awaiting-human`** — the delegation loop owns the PR |",
          "| `review:awaiting-human` + fix-delegation label(s) | pass decided `human-required` (rules 2–5), no delegation actually in flight | **remove `review:needs-worker` + `review:fixing`** — the human hand-off wins; clear stale delegation labels |",
          "| `status:needs-attention` + live progressing delegation | mode `auto-merge`, `review:fixing` + `review:needs-worker` present | **remove `status:needs-attention`** — contradicts the live progress signal |",
          "",
          "Resolution is **conservative about human intent**: a genuine",
          "`human-required` pass clears the fix-delegation labels and keeps",
          "the human-facing label; an `auto-merge` pass with a live",
          "delegation keeps the delegation labels and clears the stray",
          "`review:awaiting-human`. The reconciler only ever **removes** a",
          "contradicting label — never adds one.",
          "",
          "### Sticky `## Reviewer notes` Comment",
          "",
          "Every PR has **one** canonical reviewer-notes comment. The",
          "reviewer creates it on the first pass, then **edits it in place**",
          "on every subsequent pass via",
          "`gh api .../issues/comments/<id> -X PATCH`. It is never",
          "duplicated and never replaced by a fresh per-pass summary.",
          "",
          "This sticky comment is the **single human-facing source of truth**",
          "for the PR's current state. Humans scanning the PR should read",
          "the sticky first, before scrolling back through individual threads.",
          "It carries, at a minimum:",
          "",
          "- **Mode** — `auto-merge` or `human-required`, with the Phase 2.75",
          "  reason that chose that mode.",
          "- **AC status** — met, partial, or missing, with evidence links",
          "  to files or tests.",
          "- **CI status** — green, pending, or red.",
          "- **Outstanding** — comments still carrying a non-terminal",
          "  reviewer reaction (`eyes`, open `thinking_face`).",
          "- **Pushbacks** — every unresolved `thinking_face` the reviewer",
          "  has left, with the reason captured in its pushback reply.",
          "- **Label reconciliation** — the Phase 2.8 outcome: `consistent`",
          "  or the corrections applied this pass (violation combo, labels",
          "  removed, resulting self-consistent set), recorded for audit.",
          "- **Last pass** — the ISO 8601 timestamp of the most recent run.",
          "",
          "The sticky is updated on every pass — including passes that ended",
          "in a pushback-gated skip, a `NEEDS_CHANGES` findings comment, or",
          "a `human-required` hand-off — so it never goes stale while the",
          "reviewer is actively processing the PR.",
          "",
          "### Label Glossary",
          "",
          "Seven review-workflow labels drive the feedback loop. They are",
          "**contributed by the pr-review bundle's label set** and auto-sync",
          "into every consumer's label registry — the same way the",
          "`type:*` / `priority:*` / `status:*` taxonomy ships. Consumers do",
          "**not** hand-declare them, so they survive a label sync run with",
          "`deleteOtherLabels: true` that would otherwise prune a",
          "locally-created label.",
          "",
          "| Label | Purpose |",
          "|-------|---------|",
          `| \`${ORIGIN_ISSUE_WORKER_LABEL}\` | PR was opened by the \`issue-worker\` agent specifically. Eligible for auto-delegation of in-scope fixes. |`,
          `| \`${ORIGIN_AGENT_LABEL}\` | **Umbrella origin label for agent-opened PRs.** Stamped by every phased analyst/writer agent (people-profile, company-profile, software-profile, customer-profile, bcm-writer, industry-discovery, business-models, standards-research, regulatory-research, maintenance-audit, meeting-analysis, agenda, research-pipeline, requirements-analyst, requirements-reviewer, requirements-writer) on the PRs it opens. Delegation-eligible on exactly the same terms as \`${ORIGIN_ISSUE_WORKER_LABEL}\` — the analyst family stamps this instead of \`${ORIGIN_ISSUE_WORKER_LABEL}\` because claiming the latter on a non-worker PR would be false. Human-authored PRs carry neither origin label and will not trigger delegation unless the reviewer is invoked with \`--allow-human-author\`. This label gates fix-**delegation** only; sweep and auto-merge eligibility have never been origin-gated. |`,
          "| `review:human-required` | Force human review regardless of what the policy would otherwise decide. The reviewer never enables auto-merge on a PR carrying this label. |",
          "| `review:auto-ok` | Force auto-merge regardless of what the policy would otherwise decide. **Also resolves outstanding `thinking_face` pushbacks** as an explicit maintainer override; the reviewer logs the override in the sticky summary. |",
          "| `review:awaiting-human` | Set by the reviewer when it completes its work on a `human-required` PR and is handing off the final merge decision. Cleared by a human (or by `review:auto-ok` flipping the PR back to `auto-merge` mode). |",
          "| `review:fixing` | **Cross-session** delegation lease. Acquired by the reviewer pass that posts a fix-list; held across the worker's run; released only by a later reviewer **confirm** pass (PR carries `review:fixing` but not `review:needs-worker`). A lease held without `review:needs-worker` past the stale threshold with no worker report is orphaned and is auto-reconciled by the maintenance sweep — it re-applies `review:needs-worker` (re-arm) so the feedback drain retries, leaving the lease for the next confirm pass and posting an audit-trail note. |",
          "| `review:needs-worker` | **Durable hand-off marker.** Applied by the reviewer alongside the fix-list so a top-level `issue-worker` session discovers the PR and pulls the work (the reviewer cannot spawn the worker in session — it may run at depth-1). Cleared by the worker when it finishes applying the fix-list, which is what triggers the reviewer's confirm pass. |",
          "",
          "### Reviewing Human-Authored PRs: the `--allow-human-author` Flag",
          "",
          "By default the reviewer only **delegates** in-scope fixes on",
          `bot-authored PRs — those carrying ${DELEGATION_ELIGIBLE_ORIGIN_PHRASE}.`,
          "Running `/review-pr <n>` or `/review-prs` on a",
          "human-authored PR still produces a full review (reactions,",
          "replies, sticky summary, and auto-merge when the policy allows",
          "it) but skips the delegation hand-off to `issue-worker` — the",
          "human author is expected to apply the fixes themselves.",
          "",
          "Pass `--allow-human-author` to opt into delegation on",
          "human-authored PRs for a single invocation:",
          "",
          "```",
          "/review-pr <pr-number> --allow-human-author",
          "/review-prs --allow-human-author",
          "```",
          "",
          "The flag does **not** persist across invocations. Subsequent",
          "invocations return to the bot-only default and require the flag",
          "to be re-supplied if delegation on a human-authored PR is desired",
          "again.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],

    // `babysit-prs` is built from the resolved policy here rather than
    // declared as a module-level const, so the consumer's
    // `ci-verification.required-workflows` is baked into the rendered
    // skill at bundle-build time. Nothing rewrites skill content after
    // the bundle is assembled.
    skills: [reviewPrSkill, reviewPrsSkill, buildBabysitPrsSkill(policy)],
    subAgents: [prReviewerSubAgent],
    labels: [
      {
        name: "type:pr-review",
        color: "5319E7",
        description: "PR review tasks",
      },
      {
        name: ORIGIN_ISSUE_WORKER_LABEL,
        color: "5319E7",
        description: "PR opened by the issue-worker agent",
      },
      {
        name: ORIGIN_AGENT_LABEL,
        color: "5319E7",
        description:
          "PR opened by an agent in this project's pipeline; eligible for autonomous fix delegation",
      },
      {
        name: "review:auto-ok",
        color: "0E8A16",
        description: "Force auto-merge regardless of policy",
      },
      {
        name: "review:human-required",
        color: "D93F0B",
        description: "Force human review regardless of policy",
      },
      {
        name: "review:awaiting-human",
        color: "FBCA04",
        description: "Reviewer handed off; awaiting human merge decision",
      },
      {
        name: "review:fixing",
        color: "D4C5F9",
        description:
          "Cross-session lease held from fix-list post until a confirm pass releases it",
      },
      {
        name: "review:needs-worker",
        color: "1D76DB",
        description:
          "Durable marker: a reviewer fix-list awaits a top-level issue-worker pass",
      },
    ],
  };
}

/**
 * Render the resolved `pathsExemptFromSize` list as a YAML bullet
 * block. Each entry becomes a four-space-indented `- "<glob>"` line
 * so it nests cleanly under `auto-merge: paths-exempt-from-size:` in
 * the rendered policy YAML. An empty list collapses to a `[]` flow
 * sequence so the YAML stays parseable when consumers explicitly
 * disable the carve-out.
 */
function renderPathsExemptFromSizeYaml(
  paths: ReadonlyArray<string>,
): ReadonlyArray<string> {
  if (paths.length === 0) {
    return ["    []"];
  }
  return paths.map((path) => `    - "${path}"`);
}

/**
 * Render the resolved `requiredWorkflows` list as a YAML bullet
 * block nested under `ci-verification: required-workflows:`. Each
 * entry becomes a four-space-indented `- "<name>"` line. An empty
 * list collapses to a `[]` flow sequence — the documented default
 * that tells the Actions-runs fallback to treat every observed run
 * for the head SHA as required.
 */
function renderRequiredWorkflowsYaml(
  workflows: ReadonlyArray<string>,
): ReadonlyArray<string> {
  if (workflows.length === 0) {
    return ["    []"];
  }
  return workflows.map((workflow) => `    - "${workflow}"`);
}

/**
 * `pr-review` bundle built with the default policy. Preserved for
 * backward compatibility with tests and consumers that import the
 * const directly. Prefer `buildPrReviewBundle(policy)` when consumer
 * overrides are in scope.
 */
export const prReviewBundle: AgentRuleBundle = buildPrReviewBundle();

/**
 * Re-exports so consumers and tests can construct a resolved policy
 * and feed it to `buildPrReviewBundle` without crossing into the
 * pr-review-policy helper module.
 */
export type { ResolvedPrReviewPolicy, PrReviewPolicyConfig };
export { resolvePrReviewPolicy };
